import { createClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';
import type { Lead, Contact, Meeting, Activity, User } from '../types';
import type {
  LeadCreateInput,
  LeadUpdateInput,
  ContactCreateInput,
  ContactUpdateInput,
  MeetingCreateInput,
  MeetingUpdateInput,
  ActivityCreateInput,
  ActivityUpdateInput,
  UserUpdateInput,
} from '../types/serviceInputs';
import { LEAD_STATUS, LEAD_STATUS_INTERNAL, CONTACT_LEAD_STATUS } from '../constants/leadStatuses';

export const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
export const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const supabaseKey = supabaseAnonKey;

// Validate Supabase configuration
if (!supabaseUrl || supabaseUrl === 'your-project-url') {
  logger.error('VITE_SUPABASE_URL is not configured correctly');
}

if (!supabaseKey || supabaseKey === 'your-anon-key') {
  logger.error('VITE_SUPABASE_ANON_KEY is not configured correctly');
}

logger.debug('Supabase initialized', {
  url: supabaseUrl,
  keyPrefix: supabaseKey?.substring(0, 20) + '...',
  urlFromEnv: !!import.meta.env.VITE_SUPABASE_URL,
  keyFromEnv: !!import.meta.env.VITE_SUPABASE_ANON_KEY
});

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  global: {
    headers: {
      'x-application-name': 'crm-ceroideas'
    },
    fetch: (url, options = {}) => {
      logger.verbose('Supabase fetch', { url });

      // Add timeout and better error handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout (reducido de 30s)

      const enhancedOptions = {
        ...options,
        signal: controller.signal,
      };

      return fetch(url, enhancedOptions)
        .then(response => {
          clearTimeout(timeoutId);

          if (!response.ok && response.status >= 500) {
            logger.error(`Supabase server error: ${response.status} ${response.statusText}`);
          }

          return response;
        })
        .catch(error => {
          clearTimeout(timeoutId);

          if (error.name === 'AbortError') {
            logger.error('Supabase request timeout (15s)');
            throw new Error('Request timeout. Please check your internet connection.');
          }

          logger.error('Supabase fetch error', error);
          throw error;
        });
    }
  }
});

// Utility functions for safe data transformation
const safeDate = (dateValue: unknown): Date => {
  if (!dateValue) return new Date();
  if (dateValue instanceof Date) return dateValue;
  if (typeof dateValue === 'string') {
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  }
  return new Date();
};

const safeOptionalDate = (dateValue: unknown): Date | undefined => {
  if (!dateValue) return undefined;
  if (dateValue instanceof Date) return dateValue;
  if (typeof dateValue === 'string') {
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return undefined;
};

// Elimina claves con valor undefined para que un UPDATE parcial no ponga
// a NULL columnas que el caller no quiso tocar.
const pruneUndefined = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  const out: Partial<T> = {};
  for (const key in obj) {
    if (obj[key] !== undefined) out[key] = obj[key];
  }
  return out;
};

const safeString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const safeNumber = (value: unknown): number => {
  if (value === null || value === undefined) return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

const safeBoolean = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  return Boolean(value);
};

// Enhanced error handling with better logging and retries
class DatabaseService {
  private async executeWithRetry<T>(
    operation: () => Promise<T>,
    context: string,
    retries: number = 3,
    delay: number = 1000
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        if (attempt > 0) {
          logger.debug(`Retrying ${context} (attempt ${attempt + 1}/${retries})`);
          await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt - 1)));
        }

        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        logger.warn(`Attempt ${attempt + 1} failed for ${context}`, { error: lastError.message });

        if (attempt === retries - 1) {
          break;
        }
      }
    }

    logger.error(`All retries failed for ${context}`, lastError);
    throw lastError;
  }

  private async executeQuery<T>(
    operation: () => Promise<{ data: any; error: any }>,
    context: string
  ): Promise<T[]> {
    return this.executeWithRetry(async () => {
      logger.verbose(`Executing query: ${context}`);
      const startTime = Date.now();

      const { data, error } = await operation();

      if (error) {
        logger.error(`Query error in ${context}`, {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw new Error(`${context} falló: ${error.message}`);
      }

      if (!Array.isArray(data)) {
        logger.warn(`${context} did not return an array, converting...`);
        return [];
      }

      const duration = Date.now() - startTime;
      logger.verbose(`Query successful: ${context}`, { duration, records: data.length });

      return data;
    }, context);
  }

  private async executeSingle<T>(
    operation: () => Promise<{ data: any; error: any }>,
    context: string
  ): Promise<T | null> {
    return this.executeWithRetry(async () => {
      logger.verbose(`Executing single query: ${context}`);
      const startTime = Date.now();

      const { data, error } = await operation();

      if (error) {
        logger.error(`Single query error in ${context}`, {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw new Error(`${context} falló: ${error.message}`);
      }

      const duration = Date.now() - startTime;
      logger.verbose(`Single query successful: ${context}`, { duration });

      return data;
    }, context);
  }

  private async executeMutation<T>(
    operation: () => Promise<{ data: any; error: any }>,
    context: string
  ): Promise<T> {
    return this.executeWithRetry(async () => {
      logger.verbose(`Executing mutation: ${context}`);
      const startTime = Date.now();

      const { data, error } = await operation();

      if (error) {
        logger.error(`Mutation error in ${context}`, {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw new Error(`${context} falló: ${error.message}`);
      }

      if (!data) {
        throw new Error(`${context} no devolvió datos`);
      }

      const duration = Date.now() - startTime;
      logger.verbose(`Mutation successful: ${context}`, { duration });

      return data;
    }, context, 2);
  }

  // Public methods for services to use
  async query<T>(
    tableName: string,
    operation: (table: any) => any,
    context: string
  ): Promise<T[]> {
    return this.executeQuery(() => operation(supabase.from(tableName)), context);
  }

  async querySingle<T>(
    tableName: string,
    operation: (table: any) => any,
    context: string
  ): Promise<T | null> {
    return this.executeSingle(() => operation(supabase.from(tableName)), context);
  }

  async mutate<T>(
    tableName: string,
    operation: (table: any) => any,
    context: string
  ): Promise<T> {
    return this.executeMutation(() => operation(supabase.from(tableName)), context);
  }

  async deleteRecord(
    tableName: string,
    operation: (table: any) => any,
    context: string
  ): Promise<void> {
    return this.executeWithRetry(async () => {
      logger.verbose(`Executing delete: ${context}`);
      const { error } = await operation(supabase.from(tableName));
      if (error) {
        logger.error(`Delete error in ${context}`, {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw new Error(`${context} falló: ${error.message}`);
      }
    }, context, 2);
  }
}

const db = new DatabaseService();

// Contacts Service with robust data transformation
class ContactsService {
  private transformContact(contact: Record<string, unknown>): Contact {
    return {
      id: safeString(contact.id),
      fullName: safeString(contact.full_name),
      email: safeString(contact.email),
      phone: safeString(contact.phone),
      company: safeString(contact.company),
      role: safeString(contact.role) || undefined,
      leadStatus: (contact.lead_status as Contact['leadStatus']) || CONTACT_LEAD_STATUS.NUEVO as Contact['leadStatus'],
      productInterest: safeString(contact.product_interest),
      owner: safeString(contact.owner),
      notes: safeString(contact.notes),
      createdAt: safeDate(contact.created_at),
      updatedAt: safeDate(contact.updated_at),
      lastActivity: safeDate(contact.last_activity),
      attachments: [],
      entityType: (contact.entity_type as Contact['entityType']) || 'person',
      parentId: contact.parent_id ? safeString(contact.parent_id) : undefined,
      website: contact.website ? safeString(contact.website) : undefined,
      logo: contact.logo ? safeString(contact.logo) : undefined,
    };
  }

  async getAll(
    includeInactive: boolean = false,
    entityType?: 'person' | 'company'
  ) {
    try {
      let query = (table: any) => {
        let q = table.select(`
          id, full_name, email, phone, company, role,
          lead_status, product_interest, owner, notes,
          created_at, updated_at, last_activity, is_active, deleted_at, deleted_by,
          entity_type, parent_id, website, logo
        `);
        if (!includeInactive) q = q.eq('is_active', true);
        if (entityType) q = q.eq('entity_type', entityType);
        return q.order('created_at', { ascending: false });
      };

      const contacts = await db.query(
        'contacts',
        query,
        includeInactive ? 'Cargando todos los contactos (incluyendo inactivos)' : 'Cargando contactos activos'
      );

      return contacts.map(contact => this.transformContact(contact));
    } catch (error) {
      logger.error('Error in ContactsService.getAll', error);
      return []; // Return empty array instead of throwing
    }
  }

  async getPersons(includeInactive: boolean = false) {
    return this.getAll(includeInactive, 'person');
  }

  async getCompanies(includeInactive: boolean = false) {
    return this.getAll(includeInactive, 'company');
  }

  // Dashboard-only: sólo trae id + lead_status para calcular pipeline
  // sin descargar notes/product_interest/etc.
  async getLeadStatusSnapshot(): Promise<Array<{ id: string; leadStatus: string }>> {
    try {
      const rows = await db.query(
        'contacts',
        (table) => table.select('id, lead_status').eq('is_active', true),
        'Snapshot de lead_status de contactos'
      );
      return rows.map((r: any) => ({
        id: safeString(r.id),
        leadStatus: safeString(r.lead_status) || 'nuevo',
      }));
    } catch (error) {
      logger.error('Error in ContactsService.getLeadStatusSnapshot', error);
      return [];
    }
  }

  async getById(id: string) {
    try {
      const contact = await db.querySingle(
        'contacts',
        (table) => table.select('*').eq('id', id).single(),
        `Cargando contacto ${id}`
      );
      
      return contact ? this.transformContact(contact) : null;
    } catch (error) {
      logger.error(`Error in ContactsService.getById(${id})`, error);
      return null;
    }
  }

  async getRecent(limit: number = 10) {
    try {
      const contacts = await db.query(
        'contacts',
        (table) => table.select('*').order('created_at', { ascending: false }).limit(limit),
        `Cargando ${limit} contactos recientes`
      );
      
      return contacts.map(contact => this.transformContact(contact));
    } catch (error) {
      logger.error('Error in ContactsService.getRecent', error);
      return [];
    }
  }

  async create(contactData: ContactCreateInput): Promise<Contact> {
    try {
      const [result] = await db.mutate(
        'contacts',
        (table) => table.insert(pruneUndefined({
          full_name: contactData.fullName,
          email: contactData.email,
          phone: contactData.phone,
          company: contactData.company,
          role: contactData.role,
          lead_status: contactData.leadStatus,
          product_interest: contactData.productInterest,
          owner: contactData.owner,
          notes: contactData.notes,
          entity_type: contactData.entityType,
          parent_id: contactData.parentId,
          website: contactData.website,
          logo: contactData.logo,
        })).select(),
        'Creando contacto'
      );

      return this.transformContact(result);
    } catch (error) {
      logger.error('Error in ContactsService.create', error);
      throw error;
    }
  }

  async update(id: string, contactData: ContactUpdateInput): Promise<Contact> {
    try {
      const [result] = await db.mutate(
        'contacts',
        (table) => table.update(pruneUndefined({
          full_name: contactData.fullName,
          email: contactData.email,
          phone: contactData.phone,
          company: contactData.company,
          role: contactData.role,
          lead_status: contactData.leadStatus,
          product_interest: contactData.productInterest,
          owner: contactData.owner,
          notes: contactData.notes,
          entity_type: contactData.entityType,
          parent_id: contactData.parentId,
          website: contactData.website,
          logo: contactData.logo,
        })).eq('id', id).select(),
        `Actualizando contacto ${id}`
      );

      return this.transformContact(result);
    } catch (error) {
      logger.error(`Error in ContactsService.update(${id}):`, error);
      throw error;
    }
  }

  /**
   * Soft delete: marca el contacto como inactivo en lugar de eliminarlo
   */
  async delete(id: string, userId?: string) {
    try {
      // Obtener el contacto actual para verificar si existe
      const existing = await this.getById(id);
      if (!existing) {
        throw new Error('Contact not found');
      }

      // Marcar como inactivo (soft delete)
      const [result] = await db.mutate(
        'contacts',
        (table) => table.update({
          is_active: false,
          deleted_at: new Date().toISOString(),
          deleted_by: userId || null
        }).eq('id', id).select(),
        `Marcando contacto ${id} como inactivo`
      );

      return result;
    } catch (error) {
      logger.error(`Error in ContactsService.delete(${id}):`, error);
      throw error;
    }
  }

  /**
   * Restaura un contacto inactivo
   */
  async restore(id: string) {
    try {
      const [result] = await db.mutate(
        'contacts',
        (table) => table.update({
          is_active: true,
          deleted_at: null,
          deleted_by: null
        }).eq('id', id).select(),
        `Restaurando contacto ${id}`
      );

      return this.transformContact(result);
    } catch (error) {
      logger.error(`Error in ContactsService.restore(${id}):`, error);
      throw error;
    }
  }

  /**
   * Obtiene solo contactos inactivos
   */
  async getInactive() {
    try {
      const contacts = await db.query(
        'contacts',
        (table) => table.select(`
          id, full_name, email, phone, company, role,
          lead_status, product_interest, owner, notes,
          created_at, updated_at, last_activity, is_active, deleted_at, deleted_by
        `).eq('is_active', false).order('deleted_at', { ascending: false }),
        'Cargando contactos inactivos'
      );

      return contacts.map(contact => this.transformContact(contact));
    } catch (error) {
      logger.error('Error in ContactsService.getInactive', error);
      return [];
    }
  }

  /**
   * Verifica si un contacto tiene relaciones (proyectos, reuniones, actividades)
   */
  async checkRelationships(id: string): Promise<{
    hasProjects: boolean;
    projectsCount: number;
    hasMeetings: boolean;
    meetingsCount: number;
    hasActivities: boolean;
    activitiesCount: number;
  }> {
    try {
      const [projectsResult, meetingsResult, activitiesResult] = await Promise.all([
        supabase.from('projects').select('id', { count: 'exact', head: true }).eq('client_id', id),
        supabase.from('meetings').select('id', { count: 'exact', head: true }).eq('contact_id', id),
        supabase.from('activities').select('id', { count: 'exact', head: true }).eq('contact_id', id)
      ]);

      const projectsCount = projectsResult.count || 0;
      const meetingsCount = meetingsResult.count || 0;
      const activitiesCount = activitiesResult.count || 0;

      return {
        hasProjects: projectsCount > 0,
        projectsCount,
        hasMeetings: meetingsCount > 0,
        meetingsCount,
        hasActivities: activitiesCount > 0,
        activitiesCount
      };
    } catch (error) {
      logger.error(`Error checking relationships for contact ${id}`, error);
      return {
        hasProjects: false,
        projectsCount: 0,
        hasMeetings: false,
        meetingsCount: 0,
        hasActivities: false,
        activitiesCount: 0
      };
    }
  }
}

// Companies Service
// Facade sobre ContactsService para que los pickers existentes que consumen
// {id, name, email, phone, website, logo} sigan funcionando sin cambios.
// Internamente las empresas viven en `contacts` con entity_type='company'.
class CompaniesService {
  async getAll() {
    const rows = await contactsService.getCompanies();
    return rows.map(c => ({
      id: c.id,
      name: c.fullName,
      email: c.email,
      phone: c.phone,
      website: c.website || '',
      logo: c.logo || '',
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      contacts: [],
      meetings: [],
      tickets: [],
    }));
  }

  async create(companyData: { name: string; email: string; phone: string; website?: string; logo?: string }) {
    const created = await contactsService.create({
      fullName: companyData.name,
      email: companyData.email || '',
      phone: companyData.phone || '',
      company: '',
      leadStatus: 'ya_es_cliente',
      productInterest: '',
      owner: '',
      notes: '',
      entityType: 'company',
      website: companyData.website,
      logo: companyData.logo,
    });
    return {
      id: created.id,
      name: created.fullName,
      email: created.email,
      phone: created.phone,
      website: created.website || '',
      logo: created.logo || '',
      createdAt: created.createdAt,
      updatedAt: created.updatedAt,
      contacts: [],
      meetings: [],
      tickets: [],
    };
  }

  async update(id: string, companyData: Partial<{ name: string; email: string; phone: string; website: string; logo: string }>) {
    const updated = await contactsService.update(id, {
      fullName: companyData.name,
      email: companyData.email,
      phone: companyData.phone,
      website: companyData.website,
      logo: companyData.logo,
    });
    return {
      id: updated.id,
      name: updated.fullName,
      email: updated.email,
      phone: updated.phone,
      website: updated.website || '',
      logo: updated.logo || '',
      createdAt: updated.createdAt,
      updatedAt: updated.updatedAt,
      contacts: [],
      meetings: [],
      tickets: [],
    };
  }

  async delete(id: string) {
    return contactsService.delete(id);
  }
}

// Activities Service
class ActivitiesService {
  private transformActivity(activity: Record<string, unknown>): Activity {
    return {
      id: safeString(activity.id),
      type: (activity.type as Activity['type']) || 'reunion',
      datetime: safeDate(activity.datetime),
      description: safeString(activity.description),
      contactId: activity.contact_id ? safeString(activity.contact_id) : undefined,
      leadId: activity.lead_id ? safeString(activity.lead_id) : undefined,
      owner: safeString(activity.owner),
      contactName: activity.contacts ? safeString((activity.contacts as Record<string, unknown>).full_name) : '',
      contactEmail: activity.contacts ? safeString((activity.contacts as Record<string, unknown>).email) : '',
      contactCompany: activity.contacts ? safeString((activity.contacts as Record<string, unknown>).company) : '',
      leadName: activity.leads ? safeString((activity.leads as Record<string, unknown>).full_name) : '',
      leadEmail: activity.leads ? safeString((activity.leads as Record<string, unknown>).email) : '',
      leadCompany: activity.leads ? safeString((activity.leads as Record<string, unknown>).company) : '',
      attachmentCount: safeNumber(activity.attachment_count),
      createdAt: safeDate(activity.created_at)
    };
  }

  async getAll() {
    try {
      // First, try querying with both contacts and leads relationships
      let activities;
      let queryError;

      try {
        const result = await supabase
          .from('activities')
          .select(`
            *,
            contacts(id, full_name, email, company),
            leads(id, full_name, email, company)
          `)
          .order('datetime', { ascending: false });

        activities = result.data;
        queryError = result.error;
      } catch (joinError) {
        logger.warn('Error al consultar con leads, intentando sin leads...', joinError);
        queryError = joinError;
      }

      // If the leads relationship fails (e.g., column doesn't exist yet), fallback to contacts only
      if (queryError && queryError.code === 'PGRST200') {
        logger.warn('Relación con leads no disponible, usando solo contactos');
        const fallbackResult = await supabase
          .from('activities')
          .select(`
            *,
            contacts(id, full_name, email, company)
          `)
          .order('datetime', { ascending: false });

        if (fallbackResult.error) throw fallbackResult.error;
        activities = fallbackResult.data;
      } else if (queryError) {
        throw queryError;
      }

      const activityIds = (activities || []).map(a => a.id);
      let attachmentCountMap: Record<string, number> = {};

      if (activityIds.length > 0) {
        const { data: attachmentCounts } = await supabase
          .from('activity_attachments')
          .select('activity_id')
          .in('activity_id', activityIds);

        if (attachmentCounts) {
          for (const row of attachmentCounts) {
            attachmentCountMap[row.activity_id] = (attachmentCountMap[row.activity_id] || 0) + 1;
          }
        }
      }

      const activitiesWithCounts = (activities || []).map(activity => ({
        ...activity,
        attachment_count: attachmentCountMap[activity.id] || 0
      }));

      return activitiesWithCounts.map(activity => this.transformActivity(activity));
    } catch (error) {
      logger.error('Error in ActivitiesService.getAll', error);
      return [];
    }
  }

  async getRecent(limit: number = 10, userId?: string) {
    try {
      logger.debug('ActivitiesService.getRecent called with:', { limit, userId });

      // Try with leads relationship first
      let activities;
      try {
        let query = supabase
          .from('activities')
          .select('*, contacts(id, full_name, email, company), leads(id, full_name, email, company)')
          .order('created_at', { ascending: false })
          .limit(limit);

        // Filter by owner if userId is provided (non-admin users)
        if (userId) {
          logger.debug('Filtering activities by owner:', userId);
          query = query.eq('owner', userId);
        } else {
          logger.debug('Loading all activities (admin mode)');
        }

        const { data, error } = await query;

        if (error) throw error;
        activities = data;
        logger.debug('Query returned', activities?.length || 0, 'activities');
      } catch (joinError: any) {
        // Fallback to contacts only if leads relationship fails
        if (joinError.code === 'PGRST200') {
          logger.warn('Relación con leads no disponible en getRecent, usando solo contactos');

          let fallbackQuery = supabase
            .from('activities')
            .select('*, contacts(id, full_name, email, company)')
            .order('created_at', { ascending: false })
            .limit(limit);

          if (userId) {
            logger.debug('Fallback: Filtering activities by owner:', userId);
            fallbackQuery = fallbackQuery.eq('owner', userId);
          }

          const { data, error } = await fallbackQuery;
          if (error) throw error;
          activities = data;
          logger.debug('Fallback query returned', activities?.length || 0, 'activities');
        } else {
          throw joinError;
        }
      }

      const transformed = activities.map(activity => this.transformActivity(activity));
      logger.debug('Transformed activities:', transformed.length);

      return transformed;
    } catch (error) {
      logger.error('Error in ActivitiesService.getRecent', error);
      return [];
    }
  }

  async create(activityData: ActivityCreateInput): Promise<Activity> {
    try {
      const insertData: Record<string, unknown> = {
        type: activityData.type,
        datetime: activityData.datetime.toISOString(),
        description: activityData.description,
        owner: activityData.owner
      };

      if (activityData.contactId) {
        insertData.contact_id = activityData.contactId;
      }

      if (activityData.leadId) {
        insertData.lead_id = activityData.leadId;
      }

      let result;
      try {
        [result] = await db.mutate(
          'activities',
          (table) => table.insert(insertData).select(`
            *,
            contacts(id, full_name, email, company),
            leads(id, full_name, email, company)
          `),
          'Creando actividad'
        );
      } catch (joinError: any) {
        if (joinError.code === 'PGRST200' || (joinError.message && joinError.message.includes('relationship'))) {
          logger.warn('Relación con leads no disponible en create, usando solo contactos');

          const fallbackData: Record<string, unknown> = {
            type: activityData.type,
            datetime: activityData.datetime.toISOString(),
            description: activityData.description,
            owner: activityData.owner
          };

          if (activityData.contactId) {
            fallbackData.contact_id = activityData.contactId;
          }

          [result] = await db.mutate(
            'activities',
            (table) => table.insert(fallbackData).select(`
              *,
              contacts(id, full_name, email, company)
            `),
            'Creando actividad (solo contactos)'
          );
        } else if (joinError.message && joinError.message.includes('column') && joinError.message.includes('lead_id')) {
          logger.warn('Columna lead_id no existe, creando actividad solo con contact_id');

          const fallbackData: Record<string, unknown> = {
            type: activityData.type,
            datetime: activityData.datetime.toISOString(),
            description: activityData.description,
            owner: activityData.owner
          };

          if (activityData.contactId) {
            fallbackData.contact_id = activityData.contactId;
          }

          [result] = await db.mutate(
            'activities',
            (table) => table.insert(fallbackData).select(`
              *,
              contacts(id, full_name, email, company)
            `),
            'Creando actividad (solo contactos)'
          );
        } else {
          throw joinError;
        }
      }

      return this.transformActivity(result);
    } catch (error) {
      logger.error('Error in ActivitiesService.create', error);
      throw error;
    }
  }

  async update(id: string, activityData: ActivityUpdateInput): Promise<Activity> {
    try {
      const updateData: Record<string, unknown> = {};

      if (activityData.type !== undefined) updateData.type = activityData.type;
      if (activityData.datetime !== undefined) updateData.datetime = activityData.datetime.toISOString();
      if (activityData.description !== undefined) updateData.description = activityData.description;
      if (activityData.contactId !== undefined) updateData.contact_id = activityData.contactId;
      if (activityData.leadId !== undefined) updateData.lead_id = activityData.leadId;
      if (activityData.owner !== undefined) updateData.owner = activityData.owner;

      // Try updating with both relationships
      let result;
      try {
        [result] = await db.mutate(
          'activities',
          (table) => table.update(updateData).eq('id', id).select(`
            *,
            contacts(id, full_name, email, company),
            leads(id, full_name, email, company)
          `),
          `Actualizando actividad ${id}`
        );
      } catch (joinError: any) {
        // Fallback to contacts only if leads relationship fails
        if (joinError.code === 'PGRST200') {
          logger.warn('Relación con leads no disponible en update, usando solo contactos');
          [result] = await db.mutate(
            'activities',
            (table) => table.update(updateData).eq('id', id).select(`
              *,
              contacts(id, full_name, email, company)
            `),
            `Actualizando actividad ${id} (solo contactos)`
          );
        } else {
          throw joinError;
        }
      }

      return this.transformActivity(result);
    } catch (error) {
      logger.error(`Error in ActivitiesService.update(${id}):`, error);
      throw error;
    }
  }

  async delete(id: string) {
    try {
      return await db.deleteRecord(
        'activities',
        (table) => table.delete().eq('id', id),
        `Eliminando actividad ${id}`
      );
    } catch (error) {
      logger.error(`Error in ActivitiesService.delete(${id}):`, error);
      throw error;
    }
  }
}

// Meetings Service
class MeetingsService {
  private transformMeeting(meeting: Record<string, unknown>): Meeting {
    return {
      id: safeString(meeting.id),
      title: safeString(meeting.title),
      datetime: safeDate(meeting.datetime),
      duration: safeNumber(meeting.duration),
      participants: Array.isArray(meeting.participants) ? (meeting.participants as string[]) : [],
      description: safeString(meeting.description),
      status: (meeting.status as Meeting['status']) || 'programada',
      meetLink: safeString(meeting.meet_link),
      contactId: safeString(meeting.contact_id),
      owner: safeString(meeting.owner),
      googleEventId: safeString(meeting.google_event_id),
      assignedUserId: safeString(meeting.assigned_user_id),
      createdBy: safeString(meeting.created_by),
      approvalStatus: (meeting.approval_status as Meeting['approvalStatus']) || 'pending',
      approvedBy: safeString(meeting.approved_by),
      approvedAt: safeOptionalDate(meeting.approved_at),
      rejectionReason: safeString(meeting.rejection_reason),
      clientEmail: safeString(meeting.client_email),
      clientName: safeString(meeting.client_name),
      createdAt: safeDate(meeting.created_at),
      updatedAt: safeDate(meeting.updated_at)
    };
  }

  async getAll() {
    try {
      const meetings = await db.query(
        'meetings',
        (table) => table.select('*').order('datetime', { ascending: false }),
        'Cargando todas las reuniones'
      );
      
      return meetings.map(meeting => this.transformMeeting(meeting));
    } catch (error) {
      logger.error('Error in MeetingsService.getAll', error);
      return [];
    }
  }

  async getUpcoming(limit: number = 10) {
    try {
      const now = new Date().toISOString();
      const meetings = await db.query(
        'meetings',
        (table) => table
          .select('id, title, datetime, duration, participants, description, status, meet_link, contact_id, owner, google_event_id, assigned_user_id, created_by, approval_status, approved_by, approved_at, rejection_reason, client_email, client_name, created_at, updated_at')
          .gte('datetime', now)
          .eq('approval_status', 'approved')
          .order('datetime', { ascending: true })
          .limit(limit),
        `Cargando ${limit} reuniones próximas`
      );

      return meetings.map(meeting => this.transformMeeting(meeting));
    } catch (error) {
      logger.error('Error in MeetingsService.getUpcoming', error);
      return [];
    }
  }

  async create(meetingData: MeetingCreateInput): Promise<Meeting> {
    try {
      const [result] = await db.mutate(
        'meetings',
        (table) => table.insert({
          title: meetingData.title,
          datetime: meetingData.datetime.toISOString(),
          duration: meetingData.duration,
          participants: meetingData.participants,
          description: meetingData.description,
          status: meetingData.status,
          meet_link: meetingData.meetLink,
          contact_id: meetingData.contactId || null,
          owner: meetingData.owner,
          google_event_id: meetingData.googleEventId,
          assigned_user_id: meetingData.assignedUserId || null,
          created_by: meetingData.createdBy,
          approval_status: meetingData.approvalStatus || 'pending',
          client_email: meetingData.clientEmail,
          client_name: meetingData.clientName
        }).select(),
        'Creando reunión'
      );
      
      return this.transformMeeting(result);
    } catch (error) {
      logger.error('Error in MeetingsService.create', error);
      throw error;
    }
  }

  async update(id: string, meetingData: MeetingUpdateInput): Promise<Meeting> {
    try {
      const updateData: Record<string, unknown> = {};

      if (meetingData.title !== undefined) updateData.title = meetingData.title;
      if (meetingData.datetime !== undefined) updateData.datetime = meetingData.datetime.toISOString();
      if (meetingData.duration !== undefined) updateData.duration = meetingData.duration;
      if (meetingData.participants !== undefined) updateData.participants = meetingData.participants;
      if (meetingData.description !== undefined) updateData.description = meetingData.description;
      if (meetingData.status !== undefined) updateData.status = meetingData.status;
      if (meetingData.meetLink !== undefined) updateData.meet_link = meetingData.meetLink;
      if (meetingData.contactId !== undefined) updateData.contact_id = meetingData.contactId || null;
      if (meetingData.owner !== undefined) updateData.owner = meetingData.owner;
      if (meetingData.googleEventId !== undefined) updateData.google_event_id = meetingData.googleEventId;
      if (meetingData.assignedUserId !== undefined) updateData.assigned_user_id = meetingData.assignedUserId || null;
      if (meetingData.approval_status !== undefined) updateData.approval_status = meetingData.approval_status;
      if (meetingData.approved_by !== undefined) updateData.approved_by = meetingData.approved_by;
      if (meetingData.approved_at !== undefined) updateData.approved_at = meetingData.approved_at.toISOString();
      if (meetingData.rejection_reason !== undefined) updateData.rejection_reason = meetingData.rejection_reason;

      const [result] = await db.mutate(
        'meetings',
        (table) => table.update(updateData).eq('id', id).select(),
        `Actualizando reunión ${id}`
      );
      
      return this.transformMeeting(result);
    } catch (error) {
      logger.error(`Error in MeetingsService.update(${id}):`, error);
      throw error;
    }
  }

  async delete(id: string) {
    try {
      return await db.deleteRecord(
        'meetings',
        (table) => table.delete().eq('id', id),
        `Eliminando reunión ${id}`
      );
    } catch (error) {
      logger.error(`Error in MeetingsService.delete(${id}):`, error);
      throw error;
    }
  }
}

// Users Service with safe date handling
class UsersService {
  private transformUser(user: Record<string, unknown>) {
    // Debug logging for user transformation
    console.log('🔄 Transforming user:', {
      id: user.id,
      name: user.name,
      email: user.email,
      created_at: user.created_at,
      last_login: user.last_login,
      created_at_type: typeof user.created_at,
      last_login_type: typeof user.last_login,
      role_id: user.role_id,
      user_role: user.roles
    });

    return {
      id: safeString(user.id),
      name: safeString(user.name),
      email: safeString(user.email),
      role: (user.role as User['role']) || 'user',
      role_id: user.role_id ? safeString(user.role_id) : undefined,
      user_role: user.roles ? { name: safeString((user.roles as Record<string, unknown>).name), display_name: safeString((user.roles as Record<string, unknown>).display_name) } : undefined,
      isActive: safeBoolean(user.is_active),
      avatar: safeString(user.avatar),
      createdAt: safeDate(user.created_at),
      updatedAt: safeDate(user.updated_at),
      lastLogin: safeOptionalDate(user.last_login),
      createdBy: safeString(user.created_by),
      has_crm_access: safeBoolean(user.has_crm_access)
    };
  }

  async getAll() {
    try {
      const users = await db.query(
        'users',
        (table) => table.select('*, roles(name, display_name)').order('created_at', { ascending: false }),
        'Cargando todos los usuarios'
      );

      return users.map(user => this.transformUser(user));
    } catch (error) {
      logger.error('Error in UsersService.getAll', error);
      return [];
    }
  }

  async getById(id: string) {
    try {
      const user = await db.querySingle(
        'users',
        (table) => table.select('*, roles(name, display_name)').eq('id', id).single(),
        `Cargando usuario ${id}`
      );

      return user ? this.transformUser(user) : null;
    } catch (error) {
      logger.error(`Error in UsersService.getById(${id}):`, error);
      return null;
    }
  }

  async update(id: string, userData: UserUpdateInput) {
    try {
      const updateData: Record<string, unknown> = {};

      if (userData.name !== undefined) updateData.name = userData.name;
      if (userData.email !== undefined) updateData.email = userData.email;
      if (userData.role !== undefined) updateData.role = userData.role;
      if (userData.isActive !== undefined) updateData.is_active = userData.isActive;
      if (userData.avatar !== undefined) updateData.avatar = userData.avatar;

      const [result] = await db.mutate(
        'users',
        (table) => table.update(updateData).eq('id', id).select(),
        `Actualizando usuario ${id}`
      );

      return this.transformUser(result);
    } catch (error) {
      logger.error(`Error in UsersService.update(${id}):`, error);
      throw error;
    }
  }
}

// Leads Service
class LeadsService {
  private transformLead(lead: Record<string, unknown>): Lead {
    return {
      id: safeString(lead.id),
      fullName: safeString(lead.full_name),
      email: safeString(lead.email),
      phone: safeString(lead.phone) || undefined,
      company: safeString(lead.company),
      role: safeString(lead.role) || undefined,
      source: (lead.source as Lead['source']) || 'manual',
      status: (lead.status as Lead['status']) || LEAD_STATUS.NUEVO,
      productInterest: safeString(lead.product_interest),
      owner: safeString(lead.owner),
      notes: safeString(lead.notes),
      formData: lead.form_data || {},
      score: safeNumber(lead.score),
      lastContactDate: safeOptionalDate(lead.last_contact_date),
      nextFollowUp: safeOptionalDate(lead.next_follow_up),
      archivedAt: safeOptionalDate(lead.archived_at),
      archivedReason: safeString(lead.archived_reason),
      archivedBy: safeString(lead.archived_by),
      autoArchived: safeBoolean(lead.auto_archived),
      eligibleForArchiveAt: safeOptionalDate(lead.eligible_for_archive_at),
      createdAt: safeDate(lead.created_at),
      updatedAt: safeDate(lead.updated_at),
      analysisStatus: (lead.analysis_status as Lead['analysisStatus']) || undefined,
      analysisVersion: lead.analysis_version ? safeString(lead.analysis_version) : undefined,
      aiAnalyzedAt: safeOptionalDate(lead.ai_analyzed_at),
      analysisStage1Result: (lead.analysis_stage1_result as Lead['analysisStage1Result']) || undefined,
      analysisStage1Category: (lead.analysis_stage1_category as Lead['analysisStage1Category']) || undefined,
      analysisStage1Reason: lead.analysis_stage1_reason ? safeString(lead.analysis_stage1_reason) : undefined,
      projectLine: (lead.project_line as Lead['projectLine']) || undefined,
      leadTier: (lead.lead_tier as Lead['leadTier']) || undefined,
      leadScoreBreakdown: (lead.lead_score_breakdown as Lead['leadScoreBreakdown']) || undefined,
      leadAnalysisSummary: lead.lead_analysis_summary ? safeString(lead.lead_analysis_summary) : undefined,
      leadRedFlags: Array.isArray(lead.lead_red_flags) ? (lead.lead_red_flags as string[]) : undefined,
      leadPainSignals: Array.isArray(lead.lead_pain_signals) ? (lead.lead_pain_signals as string[]) : undefined,
      leadFitSignals: Array.isArray(lead.lead_fit_signals) ? (lead.lead_fit_signals as string[]) : undefined,
      recommendedAction: (lead.recommended_action as Lead['recommendedAction']) || undefined,
      painLevel: (lead.pain_level as Lead['painLevel']) || undefined,
      opportunityBand: (lead.opportunity_band as Lead['opportunityBand']) || undefined,
      proposalProbability: lead.proposal_probability != null ? Number(lead.proposal_probability) : undefined,
      closeProbability: lead.close_probability != null ? Number(lead.close_probability) : undefined,
      riskLevel: (lead.risk_level as Lead['riskLevel']) || undefined,
      riskReasons: Array.isArray(lead.risk_reasons) ? (lead.risk_reasons as string[]) : undefined,
      confidenceLevel: (lead.confidence_level as Lead['confidenceLevel']) || undefined,
      probabilitySummary: lead.probability_summary ? safeString(lead.probability_summary) : undefined,
      probabilityVersion: lead.probability_version ? safeString(lead.probability_version) : undefined,
      conversationSummary: lead.conversation_summary ? safeString(lead.conversation_summary) : undefined,
      conversationFull: lead.conversation_full ? safeString(lead.conversation_full) : undefined
    };
  }

  async getAll() {
    try {
      const leads = await db.query(
        'leads',
        (table) => table.select(`
          id, full_name, email, phone, company, role, source, status,
          product_interest, owner, notes, form_data, score,
          last_contact_date, next_follow_up, archived_at, archived_reason,
          archived_by, auto_archived, eligible_for_archive_at, created_at, updated_at,
          analysis_status, analysis_version, ai_analyzed_at,
          analysis_stage1_result, analysis_stage1_category, analysis_stage1_reason,
          project_line, lead_tier, lead_score_breakdown, lead_analysis_summary,
          lead_red_flags, lead_pain_signals, lead_fit_signals,
          recommended_action, pain_level, opportunity_band,
          proposal_probability, close_probability, risk_level, risk_reasons,
          confidence_level, probability_summary, probability_version,
          conversation_summary, conversation_full
        `).not('status', 'in', `(${LEAD_STATUS_INTERNAL.ARCHIVADO},${LEAD_STATUS.CONVERTIDO},${LEAD_STATUS.DESCARTADO})`).order('created_at', { ascending: false }),
        'Cargando todos los leads activos'
      );

      return leads.map(lead => this.transformLead(lead));
    } catch (error) {
      logger.error('Error in LeadsService.getAll', error);
      return [];
    }
  }

  // Dashboard-only: cuenta por status sin traer los JSONB pesados
  // (conversation_full, lead_analysis_summary, etc). ~40 columnas → 4.
  async getStatusSnapshot(): Promise<Array<{ id: string; status: string; createdAt: Date }>> {
    try {
      const rows = await db.query(
        'leads',
        (table) => table
          .select('id, status, created_at')
          .not('status', 'in', `(${LEAD_STATUS_INTERNAL.ARCHIVADO})`),
        'Snapshot de estados de leads'
      );
      return rows.map((r: any) => ({
        id: safeString(r.id),
        status: safeString(r.status),
        createdAt: safeDate(r.created_at),
      }));
    } catch (error) {
      logger.error('Error in LeadsService.getStatusSnapshot', error);
      return [];
    }
  }

  async getAllConverted() {
    try {
      const leads = await db.query(
        'leads',
        (table) => table.select(`
          id, full_name, email, phone, company, role, source, status,
          product_interest, owner, notes, form_data, score,
          last_contact_date, next_follow_up, archived_at, archived_reason,
          archived_by, auto_archived, eligible_for_archive_at, created_at, updated_at,
          analysis_status, analysis_version, ai_analyzed_at,
          analysis_stage1_result, analysis_stage1_category, analysis_stage1_reason,
          project_line, lead_tier, lead_score_breakdown, lead_analysis_summary,
          lead_red_flags, lead_pain_signals, lead_fit_signals,
          recommended_action, pain_level, opportunity_band,
          proposal_probability, close_probability, risk_level, risk_reasons,
          confidence_level, probability_summary, probability_version,
          conversation_summary, conversation_full
        `).eq('status', LEAD_STATUS.CONVERTIDO).order('created_at', { ascending: false }),
        'Cargando leads convertidos'
      );

      return leads.map(lead => this.transformLead(lead));
    } catch (error) {
      logger.error('Error in LeadsService.getAllConverted', error);
      return [];
    }
  }

  async getAllArchived() {
    try {
      const leads = await db.query(
        'leads',
        (table) => table.select('*').eq('status', LEAD_STATUS_INTERNAL.ARCHIVADO).order('archived_at', { ascending: false }),
        'Cargando leads archivados'
      );

      return leads.map(lead => this.transformLead(lead));
    } catch (error) {
      logger.error('Error in LeadsService.getAllArchived', error);
      return [];
    }
  }

  async getAllDiscarded() {
    try {
      const leads = await db.query(
        'leads',
        (table) => table.select('*').eq('status', LEAD_STATUS.DESCARTADO).order('updated_at', { ascending: false }),
        'Cargando leads descartados'
      );

      return leads.map(lead => this.transformLead(lead));
    } catch (error) {
      logger.error('Error in LeadsService.getAllDiscarded', error);
      return [];
    }
  }

  async getStats() {
    try {
      const leads = await db.query(
        'leads',
        (table) => table.select('status').not('status', 'eq', LEAD_STATUS_INTERNAL.ARCHIVADO),
        'Cargando estadísticas de leads'
      );

      const stats: Record<string, number> = {
        [LEAD_STATUS.NUEVO]: 0,
        [LEAD_STATUS.CONTACTADO]: 0,
        [LEAD_STATUS.CALIFICADO]: 0,
        [LEAD_STATUS.CONVERTIDO]: 0,
        [LEAD_STATUS.DESCARTADO]: 0,
      };

      leads.forEach((lead: Record<string, unknown>) => {
        const s = lead.status as string;
        if (s in stats) {
          stats[s]++;
        }
      });

      return stats;
    } catch (error) {
      logger.error('Error in LeadsService.getStats', error);
      return {
        [LEAD_STATUS.NUEVO]: 0,
        [LEAD_STATUS.CONTACTADO]: 0,
        [LEAD_STATUS.CALIFICADO]: 0,
        [LEAD_STATUS.CONVERTIDO]: 0,
        [LEAD_STATUS.DESCARTADO]: 0,
      };
    }
  }

  async getEligibleForArchive() {
    try {
      const now = new Date().toISOString();
      const leads = await db.query(
        'leads',
        (table) => table.select('*')
          .eq('status', LEAD_STATUS.DESCARTADO)
          .not('eligible_for_archive_at', 'is', null)
          .lte('eligible_for_archive_at', now),
        'Cargando leads elegibles para archivo'
      );

      return leads.map(lead => this.transformLead(lead));
    } catch (error) {
      logger.error('Error in LeadsService.getEligibleForArchive', error);
      return [];
    }
  }

  async create(leadData: LeadCreateInput & { skipAI?: boolean }): Promise<Lead> {
    try {
      console.log('🔄 LeadsService.create - datos recibidos:', leadData);

      if (!leadData.fullName || !leadData.email || !leadData.company) {
        throw new Error('Faltan campos obligatorios: fullName, email, company');
      }

      const result = await db.mutate(
        'leads',
        (table) => table.insert({
          full_name: leadData.fullName,
          email: leadData.email,
          phone: leadData.phone,
          company: leadData.company,
          role: leadData.role,
          source: leadData.source,
          status: leadData.status,
          product_interest: leadData.productInterest,
          owner: leadData.owner,
          notes: leadData.notes,
          form_data: leadData.formData,
          score: leadData.score,
          last_contact_date: leadData.lastContactDate?.toISOString(),
          next_follow_up: leadData.nextFollowUp?.toISOString(),
          conversation_summary: leadData.conversationSummary ?? null,
          conversation_full: leadData.conversationFull ?? null,
          analysis_status: 'pending',
          analysis_version: 'lead_intelligence_v2'
        }).select(),
        'Creando lead'
      );

      const created = this.transformLead(Array.isArray(result) ? result[0] : result);
      console.log('✅ Lead creado exitosamente:', created);

      // triggerOnCreate arranca Stage 1 + Stage 2 (2 llamadas OpenAI). En
      // creaciones puntuales del CRM lo queremos. En imports masivos o
      // migraciones históricas el caller debe pasar skipAI:true para no
      // quemar tokens ni saturar el proxy con miles de análisis.
      if (!leadData.skipAI) {
        const { leadIntelligenceService } = await import('./leadIntelligenceService');
        leadIntelligenceService.triggerOnCreate({
          id: created.id,
          fullName: created.fullName,
          company: created.company,
          email: created.email,
          phone: created.phone,
          role: created.role,
          source: created.source,
          status: created.status,
          productInterest: created.productInterest,
          score: created.score,
          owner: created.owner,
          notes: created.notes,
          createdAt: created.createdAt,
          lastContactDate: created.lastContactDate,
          nextFollowUp: created.nextFollowUp
        });
      }

      return created;
    } catch (error) {
      logger.error('Error in LeadsService.create', error);
      logger.error('Datos que causaron el error', leadData);
      throw error;
    }
  }

  async update(id: string, leadData: LeadUpdateInput): Promise<Lead> {
    try {
      const updateData: Record<string, unknown> = {};

      if (leadData.fullName !== undefined) updateData.full_name = leadData.fullName;
      if (leadData.email !== undefined) updateData.email = leadData.email;
      if (leadData.phone !== undefined) updateData.phone = leadData.phone;
      if (leadData.company !== undefined) updateData.company = leadData.company;
      if (leadData.role !== undefined) updateData.role = leadData.role;
      if (leadData.source !== undefined) updateData.source = leadData.source;
      if (leadData.status !== undefined) updateData.status = leadData.status;
      if (leadData.productInterest !== undefined) updateData.product_interest = leadData.productInterest;
      if (leadData.owner !== undefined) updateData.owner = leadData.owner;
      if (leadData.notes !== undefined) updateData.notes = leadData.notes;
      if (leadData.formData !== undefined) updateData.form_data = leadData.formData;
      if (leadData.score !== undefined) updateData.score = leadData.score;
      if (leadData.lastContactDate !== undefined) updateData.last_contact_date = leadData.lastContactDate?.toISOString();
      if (leadData.nextFollowUp !== undefined) updateData.next_follow_up = leadData.nextFollowUp?.toISOString();
      if (leadData.conversationSummary !== undefined) updateData.conversation_summary = leadData.conversationSummary;
      if (leadData.conversationFull !== undefined) updateData.conversation_full = leadData.conversationFull;

      const [result] = await db.mutate(
        'leads',
        (table) => table.update(updateData).eq('id', id).select(),
        `Actualizando lead ${id}`
      );
      
      return this.transformLead(result);
    } catch (error) {
      logger.error(`Error in LeadsService.update(${id}):`, error);
      throw error;
    }
  }

  async recordDisposition(leadId: string, action: 'archive' | 'delete', reason: string, note: string) {
    const { error } = await supabase.rpc('record_lead_disposition', {
      p_lead_id: leadId,
      p_action: action,
      p_reason: reason,
      p_note: note || null
    });
    if (error) throw error;
  }

  async archive(leadId: string, reason: string, archivedBy: string, isAutomatic: boolean = false) {
    try {
      logger.info(`Archivando lead ${leadId}...`);

      const [result] = await db.mutate(
        'leads',
        (table) => table.update({
          status: LEAD_STATUS_INTERNAL.ARCHIVADO,
          archived_at: new Date().toISOString(),
          archived_reason: reason,
          archived_by: archivedBy,
          auto_archived: isAutomatic
        }).eq('id', leadId).select(),
        `Archivando lead ${leadId}`
      );

      return this.transformLead(result);
    } catch (error) {
      logger.error(`Error in LeadsService.archive(${leadId}):`, error);
      throw error;
    }
  }

  async unarchive(leadId: string) {
    try {
      logger.info(`Restaurando lead archivado ${leadId}...`);

      const [result] = await db.mutate(
        'leads',
        (table) => table.update({
          status: LEAD_STATUS.DESCARTADO,
          archived_at: null,
          archived_reason: null,
          archived_by: null,
          auto_archived: false
        }).eq('id', leadId).select(),
        `Restaurando lead ${leadId}`
      );

      return this.transformLead(result);
    } catch (error) {
      logger.error(`Error in LeadsService.unarchive(${leadId}):`, error);
      throw error;
    }
  }

  async bulkArchive(leadIds: string[], reason: string, archivedBy: string) {
    try {
      logger.info(`Archivando ${leadIds.length} leads en bloque...`);

      const results = await db.mutate(
        'leads',
        (table) => table.update({
          status: LEAD_STATUS_INTERNAL.ARCHIVADO,
          archived_at: new Date().toISOString(),
          archived_reason: reason,
          archived_by: archivedBy,
          auto_archived: false
        }).in('id', leadIds).select(),
        `Archivando ${leadIds.length} leads`
      );

      return Array.isArray(results) ? results.map(lead => this.transformLead(lead)) : [];
    } catch (error) {
      logger.error('Error in LeadsService.bulkArchive', error);
      throw error;
    }
  }

  async getArchivedStats() {
    try {
      const { data: stats, error } = await supabase.rpc('get_archived_stats_placeholder');

      if (error) {
        console.warn('RPC function not available, calculating manually');

        const allArchived = await this.getAllArchived();
        const now = new Date();
        const thisMonth = allArchived.filter(lead => {
          const archivedDate = lead.archivedAt ? new Date(lead.archivedAt) : null;
          return archivedDate &&
                 archivedDate.getMonth() === now.getMonth() &&
                 archivedDate.getFullYear() === now.getFullYear();
        });

        const reasonCounts: Record<string, number> = {};
        allArchived.forEach(lead => {
          const reason = lead.archivedReason || 'Sin razón especificada';
          reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
        });

        const topReasons = Object.entries(reasonCounts)
          .map(([reason, count]) => ({ reason, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        return {
          totalArchived: allArchived.length,
          archivedThisMonth: thisMonth.length,
          automaticArchiveCount: allArchived.filter(l => l.autoArchived).length,
          manualArchiveCount: allArchived.filter(l => !l.autoArchived).length,
          topReasons,
          averageDaysToArchive: 0
        };
      }

      return stats;
    } catch (error) {
      logger.error('Error in LeadsService.getArchivedStats', error);
      return {
        totalArchived: 0,
        archivedThisMonth: 0,
        automaticArchiveCount: 0,
        manualArchiveCount: 0,
        topReasons: [],
        averageDaysToArchive: 0
      };
    }
  }

  async convertToContact(leadId: string, contactData: any) {
    try {
      logger.debug('Convirtiendo lead a contacto...');

      // Check idempotencia: si el email ya existe como contact, no
      // duplicamos. El trigger convert_lead_on_proposal_accept puede haber
      // llegado antes por la vía de aceptación de propuesta.
      const { data: existingContact } = await supabase
        .from('contacts')
        .select('*')
        .eq('email', contactData.email)
        .maybeSingle();

      let contactRow: any = existingContact;
      if (!contactRow) {
        const { data: newContact, error: contactError } = await supabase
          .from('contacts')
          .insert({
            full_name: contactData.fullName,
            email: contactData.email,
            phone: contactData.phone,
            company: contactData.company,
            role: contactData.role,
            lead_status: contactData.leadStatus,
            product_interest: contactData.productInterest,
            owner: contactData.owner,
            notes: contactData.notes
          })
          .select()
          .single();

        if (contactError) {
          throw new Error(`Error creando contacto: ${contactError.message}`);
        }
        contactRow = newContact;
      }

      const { error: leadError } = await supabase
        .from('leads')
        .update({
          status: LEAD_STATUS.CONVERTIDO,
          converted_to_contact_id: contactRow.id
        })
        .eq('id', leadId);

      if (leadError) {
        throw new Error(`Error actualizando lead: ${leadError.message}`);
      }

      return this.transformContact(contactRow);
    } catch (error) {
      logger.error(`Error in LeadsService.convertToContact(${leadId}):`, error);
      throw error;
    }
  }
}

// System Config Service
class SystemConfigService {
  async get(configKey: string) {
    try {
      return await db.querySingle(
        'system_config',
        (table) => table.select('config_value').eq('config_key', configKey).single(),
        `Cargando configuración ${configKey}`
      );
    } catch (error) {
      logger.error(`Error in SystemConfigService.get(${configKey}):`, error);
      return null;
    }
  }

  async set(configKey: string, configValue: any, description?: string) {
    try {
      return await db.mutate(
        'system_config',
        (table) => table.upsert({
          config_key: configKey,
          config_value: configValue,
          description: description
        }, { onConflict: 'config_key' }),
        `Guardando configuración ${configKey}`
      );
    } catch (error) {
      logger.error(`Error in SystemConfigService.set(${configKey}):`, error);
      throw error;
    }
  }
}

// Registration Requests Service
class RegistrationRequestsService {
  private transformRequest(request: any) {
    return {
      id: safeString(request.id),
      name: safeString(request.name),
      email: safeString(request.email),
      passwordHash: safeString(request.password_hash),
      status: request.status || 'pending',
      requestedAt: safeDate(request.requested_at),
      reviewedAt: safeOptionalDate(request.reviewed_at),
      reviewedBy: safeString(request.reviewed_by),
      rejectionReason: safeString(request.rejection_reason),
      createdAt: safeDate(request.created_at),
      updatedAt: safeDate(request.updated_at)
    };
  }

  async getAll() {
    try {
      const requests = await db.query(
        'registration_requests',
        (table) => table.select('*').order('requested_at', { ascending: false }),
        'Cargando solicitudes de registro'
      );
      
      return requests.map(request => this.transformRequest(request));
    } catch (error) {
      logger.error('Error in RegistrationRequestsService.getAll', error);
      return [];
    }
  }

  async create(requestData: any) {
    try {
      const [result] = await db.mutate(
        'registration_requests',
        (table) => table.insert({
          name: requestData.name,
          email: requestData.email
        }).select(),
        'Creando solicitud de registro'
      );

      return this.transformRequest(result);
    } catch (error) {
      logger.error('Error in RegistrationRequestsService.create', error);
      throw error;
    }
  }

  async approve(id: string, reviewedBy: string) {
    try {
      return await db.mutate(
        'registration_requests',
        (table) => table.update({
          status: 'approved',
          reviewed_by: reviewedBy,
          reviewed_at: new Date().toISOString()
        }).eq('id', id),
        `Aprobando solicitud ${id}`
      );
    } catch (error) {
      logger.error(`Error in RegistrationRequestsService.approve(${id}):`, error);
      throw error;
    }
  }

  async reject(id: string, reviewedBy: string, reason?: string) {
    try {
      return await db.mutate(
        'registration_requests',
        (table) => table.update({
          status: 'rejected',
          reviewed_by: reviewedBy,
          reviewed_at: new Date().toISOString(),
          rejection_reason: reason
        }).eq('id', id),
        `Rechazando solicitud ${id}`
      );
    } catch (error) {
      logger.error(`Error in RegistrationRequestsService.reject(${id}):`, error);
      throw error;
    }
  }
}

// Team Members Services (formerly Collaborators - now uses users table)
class TeamMembersService {
  private transformTeamMember(user: any) {
    return {
      id: safeString(user.id),
      name: safeString(user.name),
      email: safeString(user.email),
      country: safeString(user.country),
      hourlyRate: safeNumber(user.hourly_rate),
      paymentMethod: user.payment_method || 'bank_transfer',
      currency: user.currency || 'EUR',
      isActive: safeBoolean(user.is_active),
      createdAt: safeDate(user.created_at),
      updatedAt: safeDate(user.updated_at),
      role: user.role || 'user',
      phone: safeString(user.phone),
      avatar: user.avatar ? safeString(user.avatar) : undefined,
    };
  }

  async getAll() {
    try {
      const teamMembers = await db.query(
        'users',
        (table) => table.select('*').order('created_at', { ascending: false }),
        'Cargando miembros del equipo'
      );

      return teamMembers.map(user => this.transformTeamMember(user));
    } catch (error) {
      logger.error('Error in TeamMembersService.getAll', error);
      return [];
    }
  }

  async getTeamMembersWithBillingInfo() {
    try {
      const teamMembers = await db.query(
        'users',
        (table) => table.select('*')
          .not('hourly_rate', 'is', null)
          .order('created_at', { ascending: false }),
        'Cargando miembros del equipo con info de facturación'
      );

      return teamMembers.map(user => this.transformTeamMember(user));
    } catch (error) {
      logger.error('Error in TeamMembersService.getTeamMembersWithBillingInfo', error);
      return [];
    }
  }

  async create(memberData: any) {
    try {
      // Ahora pasamos por el edge fn create_team_member que reserva primero
      // el auth.user (id sincronizado con public.users). Antes se insertaba
      // en public.users con UUID autogenerado, y luego accept_invitation
      // creaba un auth.user con id distinto → doble fila en public.users.
      const { data, error } = await supabase.functions.invoke('create_team_member', {
        body: {
          name: memberData.name,
          email: memberData.email,
          country: memberData.country || '',
          phone: memberData.phone || null,
          hourly_rate: memberData.hourlyRate || 0,
          payment_method: memberData.paymentMethod || 'bank_transfer',
          currency: memberData.currency || 'EUR',
          is_active: memberData.isActive !== false,
        },
      });

      if (error) throw new Error(error.message || 'Error invocando create_team_member');
      if (!data?.success) {
        const reason = data?.error === 'email_already_exists'
          ? 'Ya existe un miembro activo con ese email.'
          : (data?.details || data?.error || 'Error creando miembro');
        throw new Error(reason);
      }

      return this.transformTeamMember(data.user);
    } catch (error) {
      logger.error('Error in TeamMembersService.create', error);
      throw error;
    }
  }

  async update(userId: string, memberData: any) {
    try {
      const updateData: any = {};

      if (memberData.name !== undefined) updateData.name = memberData.name;
      if (memberData.email !== undefined) updateData.email = memberData.email;
      if (memberData.country !== undefined) updateData.country = memberData.country;
      if (memberData.hourlyRate !== undefined) updateData.hourly_rate = memberData.hourlyRate;
      if (memberData.paymentMethod !== undefined) updateData.payment_method = memberData.paymentMethod;
      if (memberData.currency !== undefined) updateData.currency = memberData.currency;
      if (memberData.isActive !== undefined) updateData.is_active = memberData.isActive;
      if (memberData.phone !== undefined) updateData.phone = memberData.phone;

      const [result] = await db.mutate(
        'users',
        (table) => table.update(updateData).eq('id', userId).select(),
        `Actualizando miembro del equipo ${userId}`
      );

      return this.transformTeamMember(result);
    } catch (error) {
      logger.error(`Error in TeamMembersService.update(${userId}):`, error);
      throw error;
    }
  }

  async updateBillingInfo(userId: string, billingData: any) {
    try {
      const updateData: any = {};

      if (billingData.country !== undefined) updateData.country = billingData.country;
      if (billingData.hourlyRate !== undefined) updateData.hourly_rate = billingData.hourlyRate;
      if (billingData.paymentMethod !== undefined) updateData.payment_method = billingData.paymentMethod;
      if (billingData.currency !== undefined) updateData.currency = billingData.currency;
      if (billingData.phone !== undefined) updateData.phone = billingData.phone;

      const [result] = await db.mutate(
        'users',
        (table) => table.update(updateData).eq('id', userId).select(),
        `Actualizando información de facturación del usuario ${userId}`
      );

      return this.transformTeamMember(result);
    } catch (error) {
      logger.error(`Error in TeamMembersService.updateBillingInfo(${userId}):`, error);
      throw error;
    }
  }

  async delete(userId: string) {
    try {
      await db.deleteRecord(
        'users',
        (table) => table.delete().eq('id', userId),
        `Eliminando miembro del equipo ${userId}`
      );
    } catch (error) {
      logger.error(`Error in TeamMembersService.delete(${userId}):`, error);
      throw error;
    }
  }
}

// Legacy CollaboratorsService - kept for backwards compatibility during migration
// @deprecated Use TeamMembersService instead
class CollaboratorsService extends TeamMembersService {
  constructor() {
    super();
    logger.warn('CollaboratorsService is deprecated. Use TeamMembersService instead.');
  }
}

class ProjectsService {
  private transformProject(project: any) {
    return {
      id: safeString(project.id),
      name: safeString(project.name),
      clientId: safeString(project.client_id),
      clientName: safeString(project.client_name),
      description: safeString(project.description),
      status: project.status || 'active',
      createdAt: safeDate(project.created_at),
      updatedAt: safeDate(project.updated_at),
      createdBy: project.created_by ? safeString(project.created_by) : undefined,
      assignedUsers: project.assigned_users || [],
      // Datos económicos y de planificación — antes se guardaban pero
      // no se leían (transform los descartaba, así la vista de detalle
      // aparecía vacía aunque el modal los persistiera).
      budget: project.budget != null ? Number(project.budget) : undefined,
      totalHours: project.total_hours != null ? Number(project.total_hours) : undefined,
      hourlyRate: project.hourly_rate != null ? Number(project.hourly_rate) : undefined,
      startDate: project.start_date || undefined,
      endDate: project.end_date || undefined,
      durationMonths: project.duration_months != null ? Number(project.duration_months) : undefined,
      techStack: project.tech_stack || undefined,
      milestones: project.milestones || undefined,
    };
  }

  async getAll() {
    try {
      const { data: projects, error } = await supabase.rpc('get_visible_projects');

      if (error) {
        logger.error('Error in ProjectsService.getAll', error);
        return [];
      }

      return (projects || []).map((project: any) => {
        const assignedUserIds = (project.project_assignments || [])
          .map((a: any) => a.user_id)
          .filter(Boolean);

        return this.transformProject({
          ...project,
          assigned_users: assignedUserIds
        });
      });
    } catch (error) {
      logger.error('Error in ProjectsService.getAll', error);
      return [];
    }
  }

  async create(projectData: any) {
    try {
      // Persistimos TODAS las columnas que el modal rellena. Antes solo se
      // guardaban name/client/description/status/created_by/assigned_users —
      // budget, total_hours, hourly_rate, start_date, end_date,
      // duration_months, tech_stack, milestones se descartaban silenciosos,
      // así que el admin creía guardar presupuesto y milestones y luego no
      // aparecían en ningún sitio.
      const [result] = await db.mutate(
        'projects',
        (table) => table.insert(pruneUndefined({
          name: projectData.name,
          client_id: projectData.clientId,
          client_name: projectData.clientName,
          description: projectData.description,
          status: projectData.status,
          created_by: projectData.createdBy,
          assigned_users: projectData.assignedUsers || [],
          budget: projectData.budget,
          total_hours: projectData.totalHours,
          hourly_rate: projectData.hourlyRate,
          start_date: projectData.startDate,
          end_date: projectData.endDate,
          duration_months: projectData.durationMonths,
          tech_stack: projectData.techStack,
          milestones: projectData.milestones,
        })).select(),
        'Creando proyecto'
      );

      if (projectData.assignedUsers && projectData.assignedUsers.length > 0) {
        await this.syncProjectAssignments(result.id, projectData.assignedUsers);
      }

      return this.transformProject(result);
    } catch (error) {
      logger.error('Error in ProjectsService.create', error);
      throw error;
    }
  }

  async update(id: string, projectData: any) {
    try {
      const [result] = await db.mutate(
        'projects',
        (table) => table.update(pruneUndefined({
          name: projectData.name,
          client_id: projectData.clientId,
          client_name: projectData.clientName,
          description: projectData.description,
          status: projectData.status,
          assigned_users: projectData.assignedUsers,
          budget: projectData.budget,
          total_hours: projectData.totalHours,
          hourly_rate: projectData.hourlyRate,
          start_date: projectData.startDate,
          end_date: projectData.endDate,
          duration_months: projectData.durationMonths,
          tech_stack: projectData.techStack,
          milestones: projectData.milestones,
        })).eq('id', id).select(),
        `Actualizando proyecto ${id}`
      );

      if (projectData.assignedUsers !== undefined) {
        await this.syncProjectAssignments(id, projectData.assignedUsers);
      }

      return this.transformProject(result);
    } catch (error) {
      logger.error(`Error in ProjectsService.update(${id}):`, error);
      throw error;
    }
  }

  async delete(id: string) {
    try {
      return await db.deleteRecord(
        'projects',
        (table) => table.delete().eq('id', id),
        `Eliminando proyecto ${id}`
      );
    } catch (error) {
      logger.error(`Error in ProjectsService.delete(${id}):`, error);
      throw error;
    }
  }

  async syncProjectAssignments(projectId: string, userIds: string[]) {
    try {
      console.log(`🔄 Sincronizando asignaciones para proyecto ${projectId}:`, userIds);

      const { data: currentAssignments } = await supabase
        .from('project_assignments')
        .select('user_id')
        .eq('project_id', projectId);

      const currentUserIds = (currentAssignments || [])
        .map((a: any) => a.user_id)
        .filter(Boolean);

      const toAdd = userIds.filter(id => !currentUserIds.includes(id));
      const toRemove = currentUserIds.filter((id: string) => !userIds.includes(id));

      logger.debug('DEBUG - toAdd:', toAdd, 'toRemove:', toRemove);

      if (toRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('project_assignments')
          .delete()
          .eq('project_id', projectId)
          .in('user_id', toRemove);

        if (deleteError) {
          logger.error('Error removiendo asignaciones', deleteError);
        } else {
          logger.info(`Removidas ${toRemove.length} asignaciones`);
        }
      }

      if (toAdd.length > 0) {
        const newAssignments = toAdd.map(userId => ({
          project_id: projectId,
          user_id: userId,
          role: 'member'
        }));

        const { data: insertedData, error: insertError } = await supabase
          .from('project_assignments')
          .insert(newAssignments)
          .select();

        if (insertError) {
          logger.error('Error insertando asignaciones', insertError);
          throw insertError;
        } else {
          console.log(`✅ Añadidas ${newAssignments.length} nuevas asignaciones:`, insertedData);
        }
      }

      logger.info(`Asignaciones sincronizadas para proyecto ${projectId}`);
    } catch (error) {
      logger.error(`Error sincronizando asignaciones para proyecto ${projectId}`, error);
      throw error;
    }
  }
}

class TimesheetsService {
  private transformTimesheet(timesheet: any) {
    const userName = timesheet.users?.name || 'Usuario desconocido';
    return {
      id: safeString(timesheet.id),
      userId: safeString(timesheet.user_id),
      userName,
      // Alias legacy: el resto de la UI (TimesheetsList / TimesheetModal /
      // emailNotificationService) referencia estos nombres. Los mantenemos
      // como espejo de userId/userName mientras convivan ambos.
      collaboratorId: safeString(timesheet.user_id),
      collaboratorName: userName,
      projectId: safeString(timesheet.project_id),
      projectName: timesheet.projects?.name || 'Proyecto desconocido',
      date: safeDate(timesheet.date),
      maintenanceContractId: timesheet.maintenance_contract_id,
      workCategory: timesheet.work_category,
      maintenanceType: timesheet.maintenance_type,
      requestSource: timesheet.request_source,
      hours: safeNumber(timesheet.hours),
      appliedRate: safeNumber(timesheet.applied_rate),
      calculatedAmount: safeNumber(timesheet.calculated_amount),
      description: safeString(timesheet.description),
      status: timesheet.status || 'draft',
      createdAt: safeDate(timesheet.created_at),
      updatedAt: safeDate(timesheet.updated_at),
      approvedBy: safeString(timesheet.approved_by),
      approvedAt: safeOptionalDate(timesheet.approved_at)
    };
  }

  async getAllWithDetails(currentUserId?: string) {
    try {
      let query = supabase
        .from('timesheets')
        .select(`
          *,
          users(name),
          projects(name)
        `)
        .order('date', { ascending: false });

      // Filter by user if provided (for non-admin users)
      if (currentUserId) {
        query = query.eq('user_id', currentUserId);
      }

      const { data: timesheets, error } = await query;

      if (error) {
        logger.error('Error al cargar timesheets', error);
        return [];
      }

      return (timesheets || []).map(timesheet => this.transformTimesheet(timesheet));
    } catch (error) {
      logger.error('Error in TimesheetsService.getAllWithDetails', error);
      return [];
    }
  }

  async create(timesheetData: any) {
    try {
      const insertData = {
        user_id: timesheetData.userId,
        project_id: timesheetData.projectId,
        date: timesheetData.date.toISOString().split('T')[0],
        hours: timesheetData.hours,
        applied_rate: timesheetData.appliedRate,
        calculated_amount: timesheetData.calculatedAmount,
        description: timesheetData.description,
        status: timesheetData.status,
        maintenance_contract_id: timesheetData.maintenanceContractId || null,
        work_category: timesheetData.workCategory || 'development',
        maintenance_type: timesheetData.maintenanceType || 'support',
        request_source: timesheetData.requestSource || 'internal'
      };

      console.log('📊 TimesheetsService.create - Datos a insertar:', insertData);
      console.log('🔑 auth.uid() debería ser:', insertData.user_id);

      const [result] = await db.mutate(
        'timesheets',
        (table) => table.insert(insertData).select(),
        'Creando timesheet'
      );

      return this.transformTimesheet(result);
    } catch (error) {
      logger.error('Error in TimesheetsService.create', error);
      logger.error('Datos que intentaron insertarse', {
        user_id: timesheetData.userId,
        project_id: timesheetData.projectId
      });
      throw error;
    }
  }

  async update(id: string, timesheetData: any) {
    try {
      const updateData: any = {};
      if (timesheetData.maintenanceContractId !== undefined) updateData.maintenance_contract_id = timesheetData.maintenanceContractId;
      if (timesheetData.workCategory !== undefined) updateData.work_category = timesheetData.workCategory;
      if (timesheetData.maintenanceType !== undefined) updateData.maintenance_type = timesheetData.maintenanceType;
      if (timesheetData.requestSource !== undefined) updateData.request_source = timesheetData.requestSource;

      if (timesheetData.userId !== undefined) updateData.user_id = timesheetData.userId;
      if (timesheetData.projectId !== undefined) updateData.project_id = timesheetData.projectId;
      if (timesheetData.date !== undefined) updateData.date = timesheetData.date.toISOString().split('T')[0];
      if (timesheetData.hours !== undefined) updateData.hours = timesheetData.hours;
      if (timesheetData.appliedRate !== undefined) updateData.applied_rate = timesheetData.appliedRate;
      if (timesheetData.calculatedAmount !== undefined) updateData.calculated_amount = timesheetData.calculatedAmount;
      if (timesheetData.description !== undefined) updateData.description = timesheetData.description;
      if (timesheetData.status !== undefined) updateData.status = timesheetData.status;
      if (timesheetData.approvedBy !== undefined) updateData.approved_by = timesheetData.approvedBy;
      if (timesheetData.approvedAt !== undefined) updateData.approved_at = timesheetData.approvedAt.toISOString();

      const [result] = await db.mutate(
        'timesheets',
        (table) => table.update(updateData).eq('id', id).select(),
        `Actualizando timesheet ${id}`
      );

      return this.transformTimesheet(result);
    } catch (error) {
      logger.error(`Error in TimesheetsService.update(${id}):`, error);
      throw error;
    }
  }

  async delete(id: string) {
    try {
      return await db.deleteRecord(
        'timesheets',
        (table) => table.delete().eq('id', id),
        `Eliminando timesheet ${id}`
      );
    } catch (error) {
      logger.error(`Error in TimesheetsService.delete(${id}):`, error);
      throw error;
    }
  }
}

class UserInvoicesService {
  private transformInvoice(invoice: any) {
    return {
      id: safeString(invoice.id),
      userId: safeString(invoice.user_id),
      userName: invoice.users?.name || 'Usuario desconocido',
      invoiceNumber: safeString(invoice.invoice_number),
      date: safeDate(invoice.date),
      amount: safeNumber(invoice.amount),
      currency: invoice.currency || 'EUR',
      attachmentUrl: safeString(invoice.attachment_url),
      attachmentName: safeString(invoice.attachment_name),
      status: invoice.status || 'received',
      rejectionReason: safeString(invoice.rejection_reason),
      timesheetIds: Array.isArray(invoice.timesheet_ids) ? invoice.timesheet_ids : [],
      createdAt: safeDate(invoice.created_at),
      updatedAt: safeDate(invoice.updated_at),
      validatedBy: safeString(invoice.validated_by),
      validatedAt: safeOptionalDate(invoice.validated_at)
    };
  }

  async getAll() {
    try {
      const invoices = await db.query(
        'user_invoices',
        (table) => table.select(`
          *,
          users(name)
        `).order('date', { ascending: false }),
        'Cargando facturas de usuarios'
      );

      return invoices.map(invoice => this.transformInvoice(invoice));
    } catch (error) {
      logger.error('Error in UserInvoicesService.getAll', error);
      return [];
    }
  }

  async create(invoiceData: any) {
    try {
      // Toleramos date como Date | string 'YYYY-MM-DD' | Date-like.
      const rawDate = invoiceData.date;
      const dateObj = rawDate instanceof Date
        ? rawDate
        : (typeof rawDate === 'string' ? new Date(rawDate + 'T00:00:00') : new Date());
      const dateStr = dateObj.toISOString().split('T')[0];

      const [result] = await db.mutate(
        'user_invoices',
        (table) => table.insert({
          user_id: invoiceData.userId,
          invoice_number: invoiceData.invoiceNumber,
          date: dateStr,
          amount: invoiceData.amount,
          currency: invoiceData.currency,
          attachment_url: invoiceData.attachmentUrl,
          attachment_name: invoiceData.attachmentName,
          status: invoiceData.status,
          timesheet_ids: invoiceData.timesheetIds
        }).select(),
        'Creando factura de usuario'
      );

      return this.transformInvoice(result);
    } catch (error) {
      logger.error('Error in UserInvoicesService.create', error);
      throw error;
    }
  }

  async update(id: string, invoiceData: any) {
    try {
      const updateData: any = {};

      if (invoiceData.userId !== undefined) updateData.user_id = invoiceData.userId;
      if (invoiceData.invoiceNumber !== undefined) updateData.invoice_number = invoiceData.invoiceNumber;
      if (invoiceData.date !== undefined) {
        const rawDate = invoiceData.date;
        const dateObj = rawDate instanceof Date
          ? rawDate
          : (typeof rawDate === 'string' ? new Date(rawDate + 'T00:00:00') : new Date());
        updateData.date = dateObj.toISOString().split('T')[0];
      }
      if (invoiceData.amount !== undefined) updateData.amount = invoiceData.amount;
      if (invoiceData.currency !== undefined) updateData.currency = invoiceData.currency;
      if (invoiceData.attachmentUrl !== undefined) updateData.attachment_url = invoiceData.attachmentUrl;
      if (invoiceData.attachmentName !== undefined) updateData.attachment_name = invoiceData.attachmentName;
      if (invoiceData.status !== undefined) updateData.status = invoiceData.status;
      if (invoiceData.timesheetIds !== undefined) updateData.timesheet_ids = invoiceData.timesheetIds;
      if (invoiceData.validatedBy !== undefined) updateData.validated_by = invoiceData.validatedBy;
      if (invoiceData.validatedAt !== undefined) updateData.validated_at = invoiceData.validatedAt.toISOString();
      if (invoiceData.rejectionReason !== undefined) updateData.rejection_reason = invoiceData.rejectionReason;

      const rows = await db.mutate<any[]>(
        'user_invoices',
        (table) => table.update(updateData).eq('id', id).select(),
        `Actualizando factura ${id}`
      );

      // Si RLS o la clave filtran la fila, .select() devuelve []. Antes
      // crasheaba con 'Cannot read properties of undefined (reading id)'
      // al transformar. Preferimos un error claro.
      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error(`No se actualizó ninguna factura con id ${id} (revisa permisos o que la fila exista).`);
      }

      return this.transformInvoice(rows[0]);
    } catch (error) {
      logger.error(`Error in UserInvoicesService.update(${id}):`, error);
      throw error;
    }
  }

  async delete(id: string) {
    try {
      return await db.deleteRecord(
        'user_invoices',
        (table) => table.delete().eq('id', id),
        `Eliminando factura ${id}`
      );
    } catch (error) {
      logger.error(`Error in UserInvoicesService.delete(${id}):`, error);
      throw error;
    }
  }

  async getByDateRange(startDate: Date, endDate: Date) {
    try {
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const invoices = await db.query(
        'user_invoices',
        (table) => table.select(`
          *,
          users(name)
        `)
        .gte('date', startDateStr)
        .lte('date', endDateStr)
        .order('date', { ascending: true }),
        `Cargando facturas entre ${startDateStr} y ${endDateStr}`
      );

      return invoices.map(invoice => this.transformInvoice(invoice));
    } catch (error) {
      logger.error('Error in UserInvoicesService.getByDateRange', error);
      return [];
    }
  }

  async getByDateRangeGroupedByUser(startDate: Date, endDate: Date) {
    try {
      const invoices = await this.getByDateRange(startDate, endDate);

      const grouped = invoices.reduce((acc, invoice) => {
        const userId = invoice.userId;
        if (!acc[userId]) {
          acc[userId] = {
            userId: userId,
            userName: invoice.userName,
            invoices: []
          };
        }
        acc[userId].invoices.push(invoice);
        return acc;
      }, {} as Record<string, { userId: string; userName: string; invoices: any[] }>);

      return Object.values(grouped);
    } catch (error) {
      logger.error('Error in UserInvoicesService.getByDateRangeGroupedByUser', error);
      return [];
    }
  }

  // Alias legacy: el export sigue llamando este nombre desde antes de
  // consolidar collaborators → users. Se mantiene la forma antigua del
  // agrupado ({ collaboratorId, collaboratorName, invoices[] }) para no
  // romper invoiceExportService y el ExportInvoicesModal.
  async getByDateRangeGroupedByCollaborator(startDate: Date, endDate: Date) {
    const grouped = await this.getByDateRangeGroupedByUser(startDate, endDate);
    return grouped.map((g: any) => ({
      collaboratorId: g.userId,
      collaboratorName: g.userName,
      invoices: g.invoices,
    }));
  }
}

class PaymentsService {
  private transformPayment(payment: any) {
    return {
      id: safeString(payment.id),
      invoiceId: safeString(payment.invoice_id),
      invoiceNumber: payment.user_invoices?.invoice_number || 'Factura desconocida',
      userId: safeString(payment.user_id),
      userName: payment.users?.name || 'Usuario desconocido',
      paymentDate: safeDate(payment.payment_date),
      amount: safeNumber(payment.amount),
      currency: payment.currency || 'EUR',
      paymentMethod: safeString(payment.payment_method),
      proofUrl: safeString(payment.proof_url),
      proofName: safeString(payment.proof_name),
      status: payment.status || 'scheduled',
      approvalStatus: payment.approval_status || 'pending',
      approvedBy: safeString(payment.approved_by),
      approvedAt: safeOptionalDate(payment.approved_at),
      rejectionReason: safeString(payment.rejection_reason),
      notes: safeString(payment.notes),
      createdAt: safeDate(payment.created_at),
      updatedAt: safeDate(payment.updated_at),
      executedBy: safeString(payment.executed_by)
    };
  }

  async getAll() {
    try {
      const payments = await db.query(
        'payments',
        (table) => table.select(`
          *,
          user_invoices(invoice_number),
          users(name)
        `).order('payment_date', { ascending: false }),
        'Cargando pagos'
      );

      return payments.map(payment => this.transformPayment(payment));
    } catch (error) {
      logger.error('Error in PaymentsService.getAll', error);
      return [];
    }
  }

  async create(paymentData: any) {
    try {
      const [result] = await db.mutate(
        'payments',
        (table) => table.insert({
          invoice_id: paymentData.invoiceId,
          user_id: paymentData.userId,
          payment_date: paymentData.paymentDate.toISOString().split('T')[0],
          amount: paymentData.amount,
          currency: paymentData.currency,
          payment_method: paymentData.paymentMethod,
          proof_url: paymentData.proofUrl,
          proof_name: paymentData.proofName,
          status: paymentData.status || 'scheduled',
          approval_status: paymentData.approvalStatus || 'pending',
          notes: paymentData.notes,
          executed_by: paymentData.executedBy
        }).select(),
        'Creando pago'
      );

      return this.transformPayment(result);
    } catch (error) {
      logger.error('Error in PaymentsService.create', error);
      throw error;
    }
  }

  async update(id: string, paymentData: any) {
    try {
      const updateData: any = {};

      if (paymentData.invoiceId !== undefined) updateData.invoice_id = paymentData.invoiceId;
      if (paymentData.userId !== undefined) updateData.user_id = paymentData.userId;
      if (paymentData.paymentDate !== undefined) updateData.payment_date = paymentData.paymentDate.toISOString().split('T')[0];
      if (paymentData.amount !== undefined) updateData.amount = paymentData.amount;
      if (paymentData.currency !== undefined) updateData.currency = paymentData.currency;
      if (paymentData.paymentMethod !== undefined) updateData.payment_method = paymentData.paymentMethod;
      if (paymentData.proofUrl !== undefined) updateData.proof_url = paymentData.proofUrl;
      if (paymentData.proofName !== undefined) updateData.proof_name = paymentData.proofName;
      if (paymentData.status !== undefined) updateData.status = paymentData.status;
      if (paymentData.notes !== undefined) updateData.notes = paymentData.notes;
      if (paymentData.executedBy !== undefined) updateData.executed_by = paymentData.executedBy;

      const [result] = await db.mutate(
        'payments',
        (table) => table.update(updateData).eq('id', id).select(),
        `Actualizando pago ${id}`
      );

      return this.transformPayment(result);
    } catch (error) {
      logger.error(`Error in PaymentsService.update(${id}):`, error);
      throw error;
    }
  }

  async approve(id: string, approvedBy: string) {
    try {
      logger.debug(`Aprobando pago ${id}...`);
      const [result] = await db.mutate(
        'payments',
        (table) => table.update({
          approval_status: 'approved',
          approved_by: approvedBy,
          approved_at: new Date().toISOString()
        }).eq('id', id).select(),
        `Aprobando pago ${id}`
      );

      return this.transformPayment(result);
    } catch (error) {
      logger.error(`Error in PaymentsService.approve(${id}):`, error);
      throw error;
    }
  }

  async reject(id: string, rejectedBy: string, reason: string) {
    try {
      logger.debug(`Rechazando pago ${id}...`);
      const [result] = await db.mutate(
        'payments',
        (table) => table.update({
          approval_status: 'rejected',
          approved_by: rejectedBy,
          approved_at: new Date().toISOString(),
          rejection_reason: reason
        }).eq('id', id).select(),
        `Rechazando pago ${id}`
      );

      return this.transformPayment(result);
    } catch (error) {
      logger.error(`Error in PaymentsService.reject(${id}):`, error);
      throw error;
    }
  }

  async execute(id: string, executedBy: string) {
    try {
      logger.debug(`Ejecutando pago ${id}...`);

      const payment = await db.querySingle(
        'payments',
        (table) => table.select('*, user_invoices(id, status)').eq('id', id).single(),
        `Obteniendo pago ${id}`
      );

      if (!payment) {
        throw new Error('Pago no encontrado');
      }

      if (payment.approval_status !== 'approved') {
        throw new Error('El pago debe estar aprobado antes de ser ejecutado');
      }

      const { data: updatedPayment, error: paymentError } = await supabase
        .from('payments')
        .update({
          status: 'executed',
          executed_by: executedBy
        })
        .eq('id', id)
        .select('*, user_invoices(invoice_number), users(name)')
        .single();

      if (paymentError) {
        throw new Error(`Error actualizando pago: ${paymentError.message}`);
      }

      const { error: invoiceError } = await supabase
        .from('user_invoices')
        .update({ status: 'paid' })
        .eq('id', payment.invoice_id);

      if (invoiceError) {
        console.error('⚠️ Error actualizando estado de factura:', invoiceError);
      }

      logger.info('Pago ejecutado y factura actualizada correctamente');
      return this.transformPayment(updatedPayment);
    } catch (error) {
      logger.error(`Error in PaymentsService.execute(${id}):`, error);
      throw error;
    }
  }

  async checkInvoiceAlreadyPaid(invoiceId: string): Promise<boolean> {
    try {
      const payments = await db.query(
        'payments',
        (table) => table.select('id, status, approval_status').eq('invoice_id', invoiceId),
        `Verificando pagos para factura ${invoiceId}`
      );

      return payments.some(
        (p: any) => p.status === 'executed' && p.approval_status === 'approved'
      );
    } catch (error) {
      logger.error(`Error in PaymentsService.checkInvoiceAlreadyPaid(${invoiceId}):`, error);
      return false;
    }
  }

  async delete(id: string) {
    try {
      return await db.deleteRecord(
        'payments',
        (table) => table.delete().eq('id', id),
        `Eliminando pago ${id}`
      );
    } catch (error) {
      logger.error(`Error in PaymentsService.delete(${id}):`, error);
      throw error;
    }
  }
}

// Reports Service
class ReportsService {
  getDateRange(period: 'month' | 'quarter' | 'year'): { start: string; end: string; label: string } {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    let start: Date;
    let end: Date;
    let label: string;

    if (period === 'month') {
      start = new Date(year, month, 1);
      end = new Date(year, month + 1, 0, 23, 59, 59);
      const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      label = `${monthNames[month]} ${year}`;
    } else if (period === 'quarter') {
      const quarter = Math.floor(month / 3);
      start = new Date(year, quarter * 3, 1);
      end = new Date(year, quarter * 3 + 3, 0, 23, 59, 59);
      label = `Q${quarter + 1} ${year}`;
    } else {
      start = new Date(year, 0, 1);
      end = new Date(year, 11, 31, 23, 59, 59);
      label = `${year}`;
    }

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
      label
    };
  }

  async getCollaboratorReports(period: 'month' | 'quarter' | 'year') {
    try {
      const { start, end, label } = this.getDateRange(period);

      logger.debug(`Generando reporte por colaborador: ${label} (${start} - ${end})`);

      const timesheets = await db.query(
        'timesheets',
        (table) => table
          .select(`
            user_id,
            hours,
            applied_rate,
            calculated_amount,
            users(name, currency)
          `)
          .gte('date', start)
          .lte('date', end)
          .in('status', ['approved', 'submitted']),
        `Cargando partes de horas para reporte (${start} - ${end})`
      );

      const collaboratorMap = new Map();

      timesheets.forEach((ts: any) => {
        const collabId = ts.user_id;
        const collabName = ts.users?.name || 'Usuario desconocido';
        const currency = ts.users?.currency || 'EUR';

        if (!collaboratorMap.has(collabId)) {
          collaboratorMap.set(collabId, {
            userId: collabId,
            userName: collabName,
            totalHours: 0,
            totalAmount: 0,
            currency: currency,
            period: label
          });
        }

        const collab = collaboratorMap.get(collabId);
        collab.totalHours += safeNumber(ts.hours);
        collab.totalAmount += safeNumber(ts.calculated_amount);
      });

      const reports = Array.from(collaboratorMap.values());
      logger.info(`Reporte por colaborador generado: ${reports.length} colaboradores`);

      return reports;
    } catch (error) {
      logger.error('Error in ReportsService.getCollaboratorReports', error);
      return [];
    }
  }

  async getProjectReports(period: 'month' | 'quarter' | 'year') {
    try {
      const { start, end } = this.getDateRange(period);

      logger.debug(`Generando reporte por proyecto (${start} - ${end})`);

      const timesheets = await db.query(
        'timesheets',
        (table) => table
          .select(`
            project_id,
            user_id,
            hours,
            calculated_amount,
            projects(name, client_name)
          `)
          .gte('date', start)
          .lte('date', end)
          .in('status', ['approved', 'submitted']),
        `Cargando partes de horas por proyecto (${start} - ${end})`
      );

      const projectMap = new Map();
      const collaboratorsByProject = new Map();

      timesheets.forEach((ts: any) => {
        const projectId = ts.project_id;
        const projectName = ts.projects?.name || 'Proyecto desconocido';
        const clientName = ts.projects?.client_name || 'Cliente desconocido';

        if (!projectMap.has(projectId)) {
          projectMap.set(projectId, {
            projectId: projectId,
            projectName: projectName,
            clientName: clientName,
            totalHours: 0,
            totalAmount: 0,
            teamMembersCount: 0
          });
          collaboratorsByProject.set(projectId, new Set());
        }

        const project = projectMap.get(projectId);
        project.totalHours += safeNumber(ts.hours);
        project.totalAmount += safeNumber(ts.calculated_amount);

        collaboratorsByProject.get(projectId).add(ts.user_id);
      });

      const reports = Array.from(projectMap.values()).map(project => ({
        ...project,
        teamMembersCount: collaboratorsByProject.get(project.projectId).size
      }));

      logger.info(`Reporte por proyecto generado: ${reports.length} proyectos`);

      return reports;
    } catch (error) {
      logger.error('Error in ReportsService.getProjectReports', error);
      return [];
    }
  }

  async getPendingInvoices() {
    try {
      logger.debug('Generando reporte de facturas pendientes');

      const invoices = await db.query(
        'user_invoices',
        (table) => table
          .select(`
            id,
            invoice_number,
            amount,
            currency,
            date,
            status,
            users(name)
          `)
          .in('status', ['received', 'validated'])
          .order('date', { ascending: true }),
        'Cargando facturas pendientes de pago'
      );

      const now = new Date();

      const pendingReports = invoices.map((invoice: any) => {
        const invoiceDate = new Date(invoice.date);
        const diffTime = now.getTime() - invoiceDate.getTime();
        const daysOverdue = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        return {
          invoiceId: invoice.id,
          userName: invoice.users?.name || 'Usuario desconocido',
          invoiceNumber: invoice.invoice_number,
          amount: safeNumber(invoice.amount),
          currency: invoice.currency || 'EUR',
          daysOverdue: daysOverdue,
          status: invoice.status
        };
      });

      logger.info(`Reporte de facturas pendientes: ${pendingReports.length} facturas`);

      return pendingReports;
    } catch (error) {
      logger.error('Error in ReportsService.getPendingInvoices', error);
      return [];
    }
  }
}

class AuthAuditLogService {
  private transformAuditLog(log: any) {
    return {
      id: safeString(log.id),
      loggedAt: safeDate(log.logged_at),
      userId: safeString(log.user_id),
      userEmail: safeString(log.user_email),
      eventType: log.event_type,
      previousUserId: log.previous_user_id ? safeString(log.previous_user_id) : undefined,
      newUserId: log.new_user_id ? safeString(log.new_user_id) : undefined,
      previousRole: safeString(log.previous_role),
      newRole: safeString(log.new_role),
      appVersion: safeString(log.app_version),
      context: safeString(log.context),
      metadata: log.metadata || {},
      severity: log.severity || 'info',
      userAgent: safeString(log.user_agent),
      ipAddress: safeString(log.ip_address),
      createdAt: safeDate(log.created_at)
    };
  }

  async getRecent(limit: number = 50) {
    try {
      const logs = await db.query(
        'auth_audit_log',
        (table) => table.select('*').order('logged_at', { ascending: false }).limit(limit),
        `Cargando ${limit} logs de auditoría recientes`
      );

      return logs.map(log => this.transformAuditLog(log));
    } catch (error) {
      logger.error('Error in AuthAuditLogService.getRecent', error);
      return [];
    }
  }

  async getByUser(userId: string, limit: number = 50) {
    try {
      const logs = await db.query(
        'auth_audit_log',
        (table) => table.select('*').eq('user_id', userId).order('logged_at', { ascending: false }).limit(limit),
        `Cargando logs de auditoría para usuario ${userId}`
      );

      return logs.map(log => this.transformAuditLog(log));
    } catch (error) {
      logger.error(`Error in AuthAuditLogService.getByUser(${userId}):`, error);
      return [];
    }
  }

  async getCriticalEvents(limit: number = 100) {
    try {
      const logs = await db.query(
        'auth_audit_log',
        (table) => table
          .select('*')
          .or('severity.eq.error,severity.eq.critical,event_type.eq.session_change_detected,event_type.eq.integrity_violation')
          .order('logged_at', { ascending: false })
          .limit(limit),
        `Cargando ${limit} eventos críticos`
      );

      return logs.map(log => this.transformAuditLog(log));
    } catch (error) {
      logger.error('Error in AuthAuditLogService.getCriticalEvents', error);
      return [];
    }
  }

  async create(logData: any) {
    try {
      const [result] = await db.mutate(
        'auth_audit_log',
        (table) => table.insert({
          user_id: logData.userId,
          user_email: logData.userEmail,
          event_type: logData.eventType,
          previous_user_id: logData.previousUserId,
          new_user_id: logData.newUserId,
          previous_role: logData.previousRole,
          new_role: logData.newRole,
          app_version: logData.appVersion,
          context: logData.context,
          metadata: logData.metadata || {},
          severity: logData.severity || 'info',
          user_agent: logData.userAgent,
          ip_address: logData.ipAddress
        }).select(),
        'Creando log de auditoría'
      );

      return this.transformAuditLog(result);
    } catch (error) {
      logger.error('Error in AuthAuditLogService.create', error);
      throw error;
    }
  }
}

class AgentCallsService {
  private transformAgentCall(call: any) {
    return {
      id: safeString(call.id),
      elevenlabs_conversation_id: safeString(call.elevenlabs_conversation_id),
      agent_name: call.agent_name || null,
      channel: call.channel || null,
      source: call.source || null,
      summary: call.summary || null,
      transcript: call.transcript || null,
      duration_seconds: call.duration_seconds || null,
      started_at: call.started_at || null,
      ended_at: call.ended_at || null,
      llm_cost: call.llm_cost || null,
      currency: call.currency || null,
      lead_id: call.lead_id || null,
      contact_id: call.contact_id || null,
      created_at: safeString(call.created_at)
    };
  }

  async getByLeadId(leadId: string) {
    try {
      const calls = await db.query(
        'agent_calls',
        (table) => table
          .select('*')
          .eq('lead_id', leadId)
          .order('created_at', { ascending: false }),
        `Cargando llamadas del asistente para lead ${leadId}`
      );

      return calls.map(call => this.transformAgentCall(call));
    } catch (error) {
      logger.error(`Error in AgentCallsService.getByLeadId(${leadId}):`, error);
      return [];
    }
  }

  async getRecent(limit: number = 20) {
    try {
      const calls = await db.query(
        'agent_calls',
        (table) => table
          .select('*')
          .order('created_at', { ascending: false })
          .limit(limit),
        `Cargando ${limit} llamadas recientes del asistente`
      );

      return calls.map(call => this.transformAgentCall(call));
    } catch (error) {
      logger.error('Error in AgentCallsService.getRecent', error);
      return [];
    }
  }
}

// Export service instances
export const contactsService = new ContactsService();
export const companiesService = new CompaniesService();
export const activitiesService = new ActivitiesService();
export const meetingsService = new MeetingsService();
export const usersService = new UsersService();
export const leadsService = new LeadsService();
export const systemConfigService = new SystemConfigService();
export const registrationRequestsService = new RegistrationRequestsService();
// New services using consolidated users table
export const teamMembersService = new TeamMembersService();
export const userInvoicesService = new UserInvoicesService();

// Legacy exports for backwards compatibility (deprecated)
export const collaboratorsService = new CollaboratorsService();
export const collaboratorInvoicesService = userInvoicesService; // Alias to new service

export const projectsService = new ProjectsService();

// -------- project_tasks / project_notes / project_documents --------
export interface ProjectTask {
  id: string;
  projectId: string;
  title: string;
  description?: string;
  status: 'backlog' | 'in_progress' | 'in_review' | 'done';
  assigneeId?: string;
  estimatedHours?: number;
  priority: 'low' | 'medium' | 'high';
  dueDate?: string;
  position: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  assignee?: { id: string; name: string; avatar?: string };
}

export interface ProjectNote {
  id: string;
  projectId: string;
  body: string;
  authorId?: string;
  authorName?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProjectDocument {
  id: string;
  projectId: string;
  name: string;
  storagePath: string;
  mimeType?: string;
  sizeBytes?: number;
  uploadedBy?: string;
  uploaderName?: string;
  createdAt: Date;
}

class ProjectTasksService {
  private transform(row: any): ProjectTask {
    const u = row.users;
    return {
      id: safeString(row.id),
      projectId: safeString(row.project_id),
      title: safeString(row.title),
      description: row.description ? safeString(row.description) : undefined,
      status: (row.status as ProjectTask['status']) || 'backlog',
      assigneeId: row.assignee_id ? safeString(row.assignee_id) : undefined,
      estimatedHours: row.estimated_hours != null ? Number(row.estimated_hours) : undefined,
      priority: (row.priority as ProjectTask['priority']) || 'medium',
      dueDate: row.due_date || undefined,
      position: safeNumber(row.position),
      createdAt: safeDate(row.created_at),
      updatedAt: safeDate(row.updated_at),
      createdBy: row.created_by ? safeString(row.created_by) : undefined,
      assignee: u ? { id: safeString(u.id), name: safeString(u.name), avatar: u.avatar ? safeString(u.avatar) : undefined } : undefined,
    };
  }

  async listByProject(projectId: string): Promise<ProjectTask[]> {
    const { data, error } = await supabase
      .from('project_tasks')
      .select('*, users:assignee_id(id, name, avatar)')
      .eq('project_id', projectId)
      .order('status')
      .order('position');
    if (error) { logger.error('tasks list', error); return []; }
    return (data || []).map((r) => this.transform(r));
  }

  async create(projectId: string, input: Partial<ProjectTask>): Promise<ProjectTask> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('project_tasks')
      .insert(pruneUndefined({
        project_id: projectId,
        title: input.title || 'Nueva tarea',
        description: input.description,
        status: input.status || 'backlog',
        assignee_id: input.assigneeId,
        estimated_hours: input.estimatedHours,
        priority: input.priority || 'medium',
        due_date: input.dueDate,
        position: input.position ?? 0,
        created_by: user?.id ?? null,
      }))
      .select('*, users:assignee_id(id, name, avatar)')
      .single();
    if (error) throw error;
    return this.transform(data);
  }

  async update(id: string, input: Partial<ProjectTask>): Promise<ProjectTask> {
    const { data, error } = await supabase
      .from('project_tasks')
      .update(pruneUndefined({
        title: input.title,
        description: input.description,
        status: input.status,
        assignee_id: input.assigneeId,
        estimated_hours: input.estimatedHours,
        priority: input.priority,
        due_date: input.dueDate,
        position: input.position,
      }))
      .eq('id', id)
      .select('*, users:assignee_id(id, name, avatar)')
      .single();
    if (error) throw error;
    return this.transform(data);
  }

  async delete(id: string) {
    const { error } = await supabase.from('project_tasks').delete().eq('id', id);
    if (error) throw error;
  }
}

class ProjectNotesService {
  private transform(row: any): ProjectNote {
    const u = row.users;
    return {
      id: safeString(row.id),
      projectId: safeString(row.project_id),
      body: safeString(row.body),
      authorId: row.author_id ? safeString(row.author_id) : undefined,
      authorName: u?.name ? safeString(u.name) : undefined,
      createdAt: safeDate(row.created_at),
      updatedAt: safeDate(row.updated_at),
    };
  }

  async listByProject(projectId: string): Promise<ProjectNote[]> {
    const { data, error } = await supabase
      .from('project_notes')
      .select('*, users:author_id(id, name)')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    if (error) { logger.error('notes list', error); return []; }
    return (data || []).map(r => this.transform(r));
  }

  async create(projectId: string, body: string): Promise<ProjectNote> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('project_notes')
      .insert({ project_id: projectId, body, author_id: user?.id ?? null })
      .select('*, users:author_id(id, name)')
      .single();
    if (error) throw error;
    return this.transform(data);
  }

  async delete(id: string) {
    const { error } = await supabase.from('project_notes').delete().eq('id', id);
    if (error) throw error;
  }
}

class ProjectDocumentsService {
  private transform(row: any): ProjectDocument {
    const u = row.users;
    return {
      id: safeString(row.id),
      projectId: safeString(row.project_id),
      name: safeString(row.name),
      storagePath: safeString(row.storage_path),
      mimeType: row.mime_type ? safeString(row.mime_type) : undefined,
      sizeBytes: row.size_bytes != null ? Number(row.size_bytes) : undefined,
      uploadedBy: row.uploaded_by ? safeString(row.uploaded_by) : undefined,
      uploaderName: u?.name ? safeString(u.name) : undefined,
      createdAt: safeDate(row.created_at),
    };
  }

  async listByProject(projectId: string): Promise<ProjectDocument[]> {
    const { data, error } = await supabase
      .from('project_documents')
      .select('*, users:uploaded_by(id, name)')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    if (error) { logger.error('docs list', error); return []; }
    return (data || []).map(r => this.transform(r));
  }

  async upload(projectId: string, file: File): Promise<ProjectDocument> {
    const { data: { user } } = await supabase.auth.getUser();
    const safeFilename = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
    const path = `${projectId}/${Date.now()}_${safeFilename}`;
    const { error: upErr } = await supabase.storage
      .from('project-documents')
      .upload(path, file, { contentType: file.type, upsert: false });
    if (upErr) throw upErr;

    const { data, error } = await supabase
      .from('project_documents')
      .insert({
        project_id: projectId,
        name: file.name,
        storage_path: path,
        mime_type: file.type || null,
        size_bytes: file.size,
        uploaded_by: user?.id ?? null,
      })
      .select('*, users:uploaded_by(id, name)')
      .single();
    if (error) throw error;
    return this.transform(data);
  }

  async getSignedUrl(storagePath: string): Promise<string | null> {
    const { data, error } = await supabase.storage
      .from('project-documents')
      .createSignedUrl(storagePath, 60 * 60);
    if (error) { logger.error('signed url', error); return null; }
    return data?.signedUrl || null;
  }

  async delete(doc: ProjectDocument) {
    await supabase.storage.from('project-documents').remove([doc.storagePath]);
    const { error } = await supabase.from('project_documents').delete().eq('id', doc.id);
    if (error) throw error;
  }
}

// ------- project_subtasks -------
export interface ProjectSubtask {
  id: string;
  taskId: string;
  title: string;
  description?: string;
  completed: boolean;
  estimatedHours?: number;
  position: number;
  createdAt: Date;
  updatedAt: Date;
}

class ProjectSubtasksService {
  private transform(row: any): ProjectSubtask {
    return {
      id: safeString(row.id),
      taskId: safeString(row.task_id),
      title: safeString(row.title),
      description: row.description ? safeString(row.description) : undefined,
      completed: safeBoolean(row.completed),
      estimatedHours: row.estimated_hours != null ? Number(row.estimated_hours) : undefined,
      position: safeNumber(row.position),
      createdAt: safeDate(row.created_at),
      updatedAt: safeDate(row.updated_at),
    };
  }

  async listByTask(taskId: string): Promise<ProjectSubtask[]> {
    const { data, error } = await supabase
      .from('project_subtasks')
      .select('*')
      .eq('task_id', taskId)
      .order('position')
      .order('created_at');
    if (error) { logger.error('subtasks list', error); return []; }
    return (data || []).map(r => this.transform(r));
  }

  async listByTasks(taskIds: string[]): Promise<Record<string, ProjectSubtask[]>> {
    if (taskIds.length === 0) return {};
    const { data, error } = await supabase
      .from('project_subtasks')
      .select('*')
      .in('task_id', taskIds)
      .order('position')
      .order('created_at');
    if (error) { logger.error('subtasks list batch', error); return {}; }
    const grouped: Record<string, ProjectSubtask[]> = {};
    (data || []).forEach((r: any) => {
      const key = String(r.task_id);
      (grouped[key] = grouped[key] || []).push(this.transform(r));
    });
    return grouped;
  }

  async create(taskId: string, input: Partial<ProjectSubtask>): Promise<ProjectSubtask> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('project_subtasks')
      .insert(pruneUndefined({
        task_id: taskId,
        title: input.title || 'Nueva subtarea',
        description: input.description,
        completed: !!input.completed,
        estimated_hours: input.estimatedHours,
        position: input.position ?? 0,
        created_by: user?.id ?? null,
      }))
      .select()
      .single();
    if (error) throw error;
    return this.transform(data);
  }

  async update(id: string, input: Partial<ProjectSubtask>): Promise<ProjectSubtask> {
    const { data, error } = await supabase
      .from('project_subtasks')
      .update(pruneUndefined({
        title: input.title,
        description: input.description,
        completed: input.completed,
        estimated_hours: input.estimatedHours,
        position: input.position,
      }))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return this.transform(data);
  }

  async delete(id: string) {
    const { error } = await supabase.from('project_subtasks').delete().eq('id', id);
    if (error) throw error;
  }
}

// ------- project_task_attachments -------
export interface ProjectTaskAttachment {
  id: string;
  taskId: string;
  name: string;
  storagePath: string;
  mimeType?: string;
  sizeBytes?: number;
  uploadedBy?: string;
  createdAt: Date;
}

class ProjectTaskAttachmentsService {
  private transform(row: any): ProjectTaskAttachment {
    return {
      id: safeString(row.id),
      taskId: safeString(row.task_id),
      name: safeString(row.name),
      storagePath: safeString(row.storage_path),
      mimeType: row.mime_type ? safeString(row.mime_type) : undefined,
      sizeBytes: row.size_bytes != null ? Number(row.size_bytes) : undefined,
      uploadedBy: row.uploaded_by ? safeString(row.uploaded_by) : undefined,
      createdAt: safeDate(row.created_at),
    };
  }

  async listByTask(taskId: string): Promise<ProjectTaskAttachment[]> {
    const { data, error } = await supabase
      .from('project_task_attachments')
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: false });
    if (error) { logger.error('task attachments list', error); return []; }
    return (data || []).map(r => this.transform(r));
  }

  async listByTasks(taskIds: string[]): Promise<Record<string, ProjectTaskAttachment[]>> {
    if (taskIds.length === 0) return {};
    const { data, error } = await supabase
      .from('project_task_attachments')
      .select('*')
      .in('task_id', taskIds);
    if (error) { logger.error('task attachments batch', error); return {}; }
    const grouped: Record<string, ProjectTaskAttachment[]> = {};
    (data || []).forEach((r: any) => {
      const key = String(r.task_id);
      (grouped[key] = grouped[key] || []).push(this.transform(r));
    });
    return grouped;
  }

  async upload(taskId: string, file: File): Promise<ProjectTaskAttachment> {
    const { data: { user } } = await supabase.auth.getUser();
    const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
    const path = `${taskId}/${Date.now()}_${safeName}`;
    const { error: upErr } = await supabase.storage
      .from('task-attachments')
      .upload(path, file, { contentType: file.type, upsert: false });
    if (upErr) throw upErr;

    const { data, error } = await supabase
      .from('project_task_attachments')
      .insert({
        task_id: taskId,
        name: file.name,
        storage_path: path,
        mime_type: file.type || null,
        size_bytes: file.size,
        uploaded_by: user?.id ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return this.transform(data);
  }

  async getSignedUrl(storagePath: string): Promise<string | null> {
    const { data, error } = await supabase.storage
      .from('task-attachments')
      .createSignedUrl(storagePath, 60 * 60);
    if (error) { logger.error('signed url task', error); return null; }
    return data?.signedUrl || null;
  }

  async delete(att: ProjectTaskAttachment) {
    await supabase.storage.from('task-attachments').remove([att.storagePath]);
    const { error } = await supabase.from('project_task_attachments').delete().eq('id', att.id);
    if (error) throw error;
  }
}

export const projectTasksService = new ProjectTasksService();
export const projectNotesService = new ProjectNotesService();
export const projectDocumentsService = new ProjectDocumentsService();
export const projectSubtasksService = new ProjectSubtasksService();
export const projectTaskAttachmentsService = new ProjectTaskAttachmentsService();

export const timesheetsService = new TimesheetsService();
export const paymentsService = new PaymentsService();
export const reportsService = new ReportsService();
export const authAuditLogService = new AuthAuditLogService();
export const agentCallsService = new AgentCallsService();